import React, { useState, useEffect } from 'react';
import { Activity, RefreshCw, Target, UserPlus, LogOut, User, Shield, TrendingUp, Trophy, XCircle, CheckCircle, BarChart3, Tag, Copy, Search, X, Share2, Gift, Users, Link, SkipForward, MapPin, Phone, Settings, ChevronDown, MessageSquare, Send, AlertTriangle, BarChart2, Clock, RotateCcw, Calculator, Check, Crown, Zap, Download } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast, Toaster } from 'sonner';
import GuestAuthModal from '../components/GuestAuthModal';
import PaymentModal from '../components/PaymentModal';
import RenewalModal from '../components/RenewalModal';
import UserProfileModal from '../components/UserProfileModal';
import { UserMessengerModal, MessengerButton } from '../components/MessengerModal';
import LanguageSelector from '../components/LanguageSelector';
import EducationTVModal, { EducationTVButton, EducationNotificationBell } from '../components/EducationTV';
import PolicySections from '../components/PolicySections';
import { PublicReviewsSection } from '../components/ReviewSystem';
import { useLanguage } from '../contexts/LanguageContext';
import ProScalperBot from '../components/ProScalperBot';
import CompoundPlanCard from '../components/CompoundPlanCard';
import UpgradeModal from '../components/UpgradeModal';
import ProUpgradePromo from '../components/ProUpgradePromo';

// Always use tradepayflow.emergent.host for bonbot.biz custom domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const GuestPortal = () => {
  const { t } = useLanguage();
  
  // Authentication
  const [guestUser, setGuestUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Education TV states
  const [showEducationTV, setShowEducationTV] = useState(false);
  const [educationNotifications, setEducationNotifications] = useState(0);

  // Messenger state
  const [showMessenger, setShowMessenger] = useState(false);

  // User Profile state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  // Pro Mode state
  const [showProMode, setShowProMode] = useState(() => {
    const saved = localStorage.getItem('showProMode');
    return saved === 'true';
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showProPromo, setShowProPromo] = useState(false);

  // Support Ticket state
  const [supportName, setSupportName] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportSuccess, setSupportSuccess] = useState(false);
  const [supportExpanded, setSupportExpanded] = useState(false);

  // Top Trader Performers state
  const [topTraderPerformers, setTopTraderPerformers] = useState([]);

  // User Referral states
  const [referralInfo, setReferralInfo] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);

  // Manual Signal Generator states
  const [manualPairs, setManualPairs] = useState([]);
  const [selectedManualPair, setSelectedManualPair] = useState('EURUSD_OTC');
  const [generatingSignal, setGeneratingSignal] = useState(false);
  const [analyzingSignal, setAnalyzingSignal] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(0);
  const [analyzeStatus, setAnalyzeStatus] = useState(''); // Status message for filtering
  const [lastManualSignalResult, setLastManualSignalResult] = useState(null);
  const [entryCountdown, setEntryCountdown] = useState(null); // Countdown to next candle entry
  
  // Trade reporting states
  const [pendingReport, setPendingReport] = useState(false);
  const [lastSignalInfo, setLastSignalInfo] = useState(null);
  const [reportingResult, setReportingResult] = useState(false);
  const [profitabilityStats, setProfitabilityStats] = useState(null);
  const [showProfitability, setShowProfitability] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('last_24h');
  
  // Indicator selection state
  const [selectedIndicators, setSelectedIndicators] = useState('all'); // 'all', 'macd', 'stochastic', 'rsi'
  
  // Anti-Volatility Protection (replaces old anti-manipulation)
  const [antiVolatilityEnabled, setAntiVolatilityEnabled] = useState(() => {
    const saved = localStorage.getItem('antiVolatilityEnabled');
    return saved !== null ? JSON.parse(saved) : true; // Default enabled
  });
  
  // Anti-volatility check results state
  const [avCheckResults, setAvCheckResults] = useState(null);
  
  // Signal Timeframe Selection
  const [signalTimeframe, setSignalTimeframe] = useState(() => {
    const saved = localStorage.getItem('signalTimeframe');
    return saved || '5'; // Default 5 minute
  });
  
  // Signal Method Selection (indicators vs udu-dud)
  const [signalMethod, setSignalMethod] = useState(() => {
    const saved = localStorage.getItem('signalMethod');
    return saved || 'indicators'; // Default to traditional indicators
  });
  
  // Auto Scanner state
  const [autoScannerEnabled, setAutoScannerEnabled] = useState(() => {
    const saved = localStorage.getItem('autoScannerEnabled');
    return saved !== null ? JSON.parse(saved) : false; // Default disabled
  });
  const [highPayoutOnly, setHighPayoutOnly] = useState(() => {
    const saved = localStorage.getItem('highPayoutOnly');
    return saved !== null ? JSON.parse(saved) : true; // Default to high payout filter
  });
  const [highPayoutAssets, setHighPayoutAssets] = useState([]);
  const [autoScannerRunning, setAutoScannerRunning] = useState(false);
  const [currentScanAsset, setCurrentScanAsset] = useState(null);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanResults, setScanResults] = useState([]);
  const [bestSignal, setBestSignal] = useState(null); // The best signal from full scan
  const [awaitingTradeReport, setAwaitingTradeReport] = useState(false); // Paused until trade reported
  const [scanComplete, setScanComplete] = useState(false);
  
  // Check if subscription is expired/paused - disable scanner and generator
  const isSubscriptionExpired = guestUser?.subscription_expired || 
                                 guestUser?.status === 'subscription_expired' || 
                                 guestUser?.status === 'paused' ||
                                 guestUser?.status === 'expired';
  
  // Low Payout Blocked Assets - persists for 15 minutes
  const [blockedLowPayoutAssets, setBlockedLowPayoutAssets] = useState(() => {
    const saved = localStorage.getItem('blockedLowPayoutAssets');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Filter out expired blocks
      const now = Date.now();
      const active = parsed.filter(item => item.expiresAt > now);
      if (active.length !== parsed.length) {
        localStorage.setItem('blockedLowPayoutAssets', JSON.stringify(active));
      }
      return active;
    }
    return [];
  });
  
  // Countdown timer ref to prevent duplicate countdowns
  const countdownIntervalRef = React.useRef(null);
  const countdownTimeoutRef = React.useRef(null);
  
  // Search/filter state for trading pairs
  const [pairSearch, setPairSearch] = useState('');
  
  // Promotion banner state
  const [promotion, setPromotion] = useState(null);
  const [promoCountdown, setPromoCountdown] = useState(null);
  
  // URL referral code state (for visitors)
  const [urlReferralCode, setUrlReferralCode] = useState(null);

  // Market Manipulation Alert state
  const [marketAlert, setMarketAlert] = useState({ enabled: false, message: '' });

  // Trading Session Manager state
  const [sessionConfig, setSessionConfig] = useState(() => {
    const saved = localStorage.getItem('tradingSessionConfig');
    return saved ? JSON.parse(saved) : {
      accountSize: 100,
      riskAmount: 10, // Stop loss for session
      profitTarget: 20,
      sessionMinutes: 30,
      payoutPercent: 92,
      totalTrades: 5,
      sessionNumber: 1,
      coolingPeriod: 15 // minutes
    };
  });
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionTrades, setSessionTrades] = useState([]);
  const [sessionStats, setSessionStats] = useState({
    currentPnL: 0,
    wins: 0,
    losses: 0,
    currentLotSize: 0,
    tradesRemaining: 0,
    requiredWins: 0,
    targetReached: false,
    stopReached: false
  });
  
  // Cooling period - persists across page refreshes
  const [coolingUntil, setCoolingUntil] = useState(() => {
    const saved = localStorage.getItem('tradingCoolingUntil');
    if (saved) {
      const coolingDate = new Date(saved);
      // Check if cooling period is still active
      if (coolingDate > new Date()) {
        return coolingDate;
      } else {
        // Cooling expired, clear it
        localStorage.removeItem('tradingCoolingUntil');
        return null;
      }
    }
    return null;
  });
  
  // Cooling reason - persists to show why cooldown was triggered
  const [coolingReason, setCoolingReason] = useState(() => {
    return localStorage.getItem('tradingCoolingReason') || null;
  });
  
  const [showSessionSetup, setShowSessionSetup] = useState(true);
  const [showCompoundPlan, setShowCompoundPlan] = useState(false);
  
  // Compound Interest Calculator state - flipable with stable assets
  const [showCompoundCalc, setShowCompoundCalc] = useState(false);
  const [compoundConfig, setCompoundConfig] = useState(() => {
    const saved = localStorage.getItem('compoundInterestConfig');
    return saved ? JSON.parse(saved) : {
      startingBalance: 100,
      dailyInterest: 10,
      durationDays: 30
    };
  });
  const [compoundPlan, setCompoundPlan] = useState(() => {
    const saved = localStorage.getItem('compoundInterestPlan');
    return saved ? JSON.parse(saved) : null;
  });
  const [compoundChecks, setCompoundChecks] = useState(() => {
    const saved = localStorage.getItem('compoundInterestChecks');
    return saved ? JSON.parse(saved) : {};
  });
  
  // Daily stats tracking - persists across sessions until reset
  const [dailyStats, setDailyStats] = useState(() => {
    const saved = localStorage.getItem('dailyTradingStats');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Check if it's from today
      const today = new Date().toDateString();
      if (parsed.date === today) {
        return parsed;
      }
    }
    // Fresh day or no saved data
    return {
      date: new Date().toDateString(),
      totalPnL: 0,
      totalWins: 0,
      totalLosses: 0,
      sessionsCompleted: 0,
      sessionResults: [] // Array of {sessionNumber, pnl, wins, losses}
    };
  });
  
  // Target wins selection for session planning
  const [targetWins, setTargetWins] = useState(null); // null = auto calculate


  // Top performers state
  const [topPerformers, setTopPerformers] = useState(null);
  const [loadingTopPerformers, setLoadingTopPerformers] = useState(false);

  // Coaching/Booking state
  const [showCoachingModal, setShowCoachingModal] = useState(false);
  const [coachingPackages, setCoachingPackages] = useState({});
  const [coachingAvailability, setCoachingAvailability] = useState([]);
  const [selectedCoachingPackage, setSelectedCoachingPackage] = useState(null);
  const [selectedCoachingDate, setSelectedCoachingDate] = useState(null);
  const [selectedCoachingTime, setSelectedCoachingTime] = useState(null);
  const [coachingStep, setCoachingStep] = useState('packages'); // packages, calendar, payment, confirmation, appointments
  const [coachingBookingId, setCoachingBookingId] = useState(null);
  const [coachingPaymentProof, setCoachingPaymentProof] = useState(null);
  const [coachingLoading, setCoachingLoading] = useState(false);
  const [viewingMonth, setViewingMonth] = useState(new Date().getMonth());
  const [viewingYear, setViewingYear] = useState(new Date().getFullYear());
  const [userCoachingBookings, setUserCoachingBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // Filter pairs based on search
  const filteredPairs = manualPairs.filter(pair => 
    pair.name.toLowerCase().includes(pairSearch.toLowerCase()) ||
    pair.symbol.toLowerCase().includes(pairSearch.toLowerCase())
  );
  
  // Helper function to format asset name with OTC badge
  const formatAssetName = (name, symbol, options = {}) => {
    const { showOtcBadge = true, removeSuffix = false } = options;
    const isOTC = symbol?.includes('_OTC') || name?.includes('OTC');
    let displayName = name || symbol || 'Unknown';
    
    // Optionally remove OTC suffix from name for cleaner display
    if (removeSuffix) {
      displayName = displayName.replace(' OTC', '').replace('_OTC', '');
    }
    
    if (showOtcBadge && isOTC) {
      return (
        <span className="inline-flex items-center gap-1.5">
          {displayName}
          <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-500/30 text-purple-300 border border-purple-500/50 rounded">
            OTC
          </span>
        </span>
      );
    }
    
    return displayName;
  };
  
  // Helper to get plain text asset name with OTC label for toasts
  const getAssetNameWithOtc = (name, symbol) => {
    const isOTC = symbol?.includes('_OTC') || name?.includes('OTC');
    let displayName = name || symbol || 'Unknown';
    displayName = displayName.replace(' OTC', '').replace('_OTC', '');
    return isOTC ? `${displayName} [OTC]` : displayName;
  };

  // Save anti-volatility setting to localStorage
  useEffect(() => {
    localStorage.setItem('antiVolatilityEnabled', JSON.stringify(antiVolatilityEnabled));
  }, [antiVolatilityEnabled]);

  // Save signal timeframe to localStorage
  useEffect(() => {
    localStorage.setItem('signalTimeframe', signalTimeframe);
  }, [signalTimeframe]);

  // Save signal method to localStorage
  useEffect(() => {
    localStorage.setItem('signalMethod', signalMethod);
  }, [signalMethod]);

  // Save auto scanner settings to localStorage
  useEffect(() => {
    localStorage.setItem('autoScannerEnabled', JSON.stringify(autoScannerEnabled));
  }, [autoScannerEnabled]);

  useEffect(() => {
    localStorage.setItem('highPayoutOnly', JSON.stringify(highPayoutOnly));
  }, [highPayoutOnly]);

  // Fetch high payout assets
  useEffect(() => {
    const fetchHighPayoutAssets = async () => {
      try {
        const response = await fetch(`${API}/assets/high-payout?min_payout=92`);
        const data = await response.json();
        if (data.success && data.assets) {
          setHighPayoutAssets(data.assets);
        }
      } catch (error) {
        console.error('Error fetching high payout assets:', error);
      }
    };
    fetchHighPayoutAssets();
  }, []);

  // Fetch market manipulation alert status
  useEffect(() => {
    const fetchMarketAlert = async () => {
      try {
        const response = await fetch(`${API}/settings/market-alert`);
        const data = await response.json();
        if (data.success) {
          setMarketAlert({
            enabled: data.alert_enabled,
            message: data.alert_message,
            severity: data.alert_severity
          });
        }
      } catch (error) {
        console.error('Error fetching market alert:', error);
      }
    };
    fetchMarketAlert();
    
    // Poll for market alert changes every 30 seconds
    const alertInterval = setInterval(fetchMarketAlert, 30000);
    return () => clearInterval(alertInterval);
  }, []);

  // Auto Scanner Logic - Scan ALL assets at once (like manual generator)
  const [scanRound, setScanRound] = useState(0);
  const [assetsScanned, setAssetsScanned] = useState(0);
  
  useEffect(() => {
    let isCancelled = false;
    let scanInterval = null;
    
    const scanAllAssets = async () => {
      // Don't run if disabled, not logged in, showing a signal, or subscription expired
      if (!autoScannerEnabled || !guestUser || awaitingTradeReport || isSubscriptionExpired) {
        setAutoScannerRunning(false);
        if (isSubscriptionExpired) {
          console.log('🚫 Auto Scanner blocked: Subscription expired');
        }
        return;
      }
      
      // Don't run if in cooling period
      if (coolingUntil && new Date() < coolingUntil) {
        setAutoScannerRunning(false);
        console.log('🚫 Auto Scanner blocked: Cooling period active');
        return;
      }
      
      // Clean up expired blocked assets first
      const now = Date.now();
      const activeBlocks = blockedLowPayoutAssets.filter(item => item.expiresAt > now);
      if (activeBlocks.length !== blockedLowPayoutAssets.length) {
        setBlockedLowPayoutAssets(activeBlocks);
        localStorage.setItem('blockedLowPayoutAssets', JSON.stringify(activeBlocks));
      }
      
      // Get blocked asset symbols for filtering
      const blockedSymbols = new Set(activeBlocks.map(item => item.symbol));
      
      // Get assets to scan - ALWAYS filter for 92%+ when highPayoutOnly is enabled
      let allAssets;
      if (highPayoutOnly) {
        // Only use assets with 92%+ payout
        allAssets = highPayoutAssets.filter(a => (a.payout || 0) >= 92);
        if (allAssets.length === 0) {
          console.log('⚠️ No 92%+ payout assets available, waiting...');
          setAutoScannerRunning(false);
          return;
        }
      } else {
        allAssets = manualPairs.map(p => ({ symbol: p.symbol, name: p.name, payout: 85 }));
      }
      
      // Filter out blocked low-payout assets
      const assetsBeforeFilter = allAssets.length;
      allAssets = allAssets.filter(a => !blockedSymbols.has(a.symbol));
      
      if (blockedSymbols.size > 0) {
        console.log(`🚫 Filtered out ${assetsBeforeFilter - allAssets.length} blocked low-payout assets (${blockedSymbols.size} total blocked)`);
      }
      
      if (allAssets.length === 0) {
        console.log('⚠️ All assets are blocked due to low payout. Waiting for blocks to expire...');
        setAutoScannerRunning(false);
        return;
      }
      
      setAutoScannerRunning(true);
      // Don't clear bestSignal if we already have one awaiting report
      if (!awaitingTradeReport) {
        setBestSignal(null);
      }
      setScanComplete(false);
      setCurrentScanAsset(null);
      
      const guestToken = localStorage.getItem('guestToken');
      const MIN_AGREEMENT = 2; // Minimum indicators that must agree
      const CONFIRMATION_SECONDS = antiVolatilityEnabled ? 15 : 5; // Longer confirmation when anti-manipulation ON
      
      console.log(`🔍 Auto Scanner: Starting scan of ${allAssets.length} assets...`);
      console.log(`🛡️ Anti Manipulation: ${antiVolatilityEnabled ? 'ON (15s confirmation)' : 'OFF (5s confirmation)'}`);
      console.log(`📊 Signal Method: ${signalMethod}`);
      setScanProgress(0);
      setAssetsScanned(0);
      
      // For UDU-DUD method, wait until at least 30 seconds of the candle has formed
      // This allows proper assessment of the 70% retracement pattern
      if (signalMethod === 'udu-dud') {
        const now = new Date();
        const secondsIntoMinute = now.getSeconds();
        const UDU_DUD_MIN_CANDLE_TIME = 30; // Wait until 30 seconds into the candle
        
        if (secondsIntoMinute < UDU_DUD_MIN_CANDLE_TIME) {
          const waitTime = UDU_DUD_MIN_CANDLE_TIME - secondsIntoMinute;
          console.log(`🕯️ UDU-DUD: Waiting ${waitTime}s for candle to form (need ${UDU_DUD_MIN_CANDLE_TIME}s of data)...`);
          setAnalyzeStatus(`🕯️ Waiting for candle formation... ${waitTime}s`);
          
          // Show countdown while waiting
          for (let i = waitTime; i > 0; i--) {
            if (isCancelled) return;
            setAnalyzeStatus(`🕯️ Reading candle pattern in ${i}s...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
          setAnalyzeStatus('🕯️ Candle ready! Scanning for patterns...');
          console.log('🕯️ UDU-DUD: Candle has enough data, starting pattern scan...');
        } else {
          console.log(`🕯️ UDU-DUD: Candle already has ${secondsIntoMinute}s of data, scanning immediately...`);
        }
      }
      
      // Scan ALL assets in parallel
      const scanPromises = allAssets.map(async (asset, index) => {
        if (isCancelled) return null;
        
        try {
          const response = await fetch(`${API}/scanner/manual-signal`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              token: guestToken,
              symbol: asset.symbol,
              indicator: 'all',
              skip_pending_check: true,
              anti_volatility: antiVolatilityEnabled,
              timeframe: signalTimeframe,
              method: signalMethod  // 'indicators' or 'udu-dud'
            })
          });
          
          if (!response.ok) return null;
          
          const data = await response.json();
          
          // Update progress
          setAssetsScanned(prev => prev + 1);
          
          // Check if signal was blocked by anti-volatility
          if (data.blocked_by_av) {
            return null; // Silently skip blocked signals
          }
          
          // Check if we got a valid signal
          if (data.success && data.signal_found && data.signal) {
            const indicators = data.indicators || {};
            const signalStrengthLevel = data.signal_strength_level || 1;
            const riskLevel = data.risk_level || 'MEDIUM';
            const isUduDud = data.signal_method === 'udu-dud' || indicators.method === 'UDU-DUD';
            
            // Debug log for UDU-DUD signals
            if (isUduDud) {
              console.log('🕯️ UDU-DUD Signal found:', {
                signal: data.signal,
                method: indicators.method,
                pattern: indicators.pattern,
                formation: indicators.formation,
                expected_next: indicators.expected_next
              });
            }
            
            // For UDU-DUD method, accept any valid signal (no indicator agreement requirement)
            // For traditional indicators, require at least 2 indicators agreeing
            if (!isUduDud && signalStrengthLevel < 2) {
              return null;
            }
            
            return {
              asset: asset,
              signal: data.signal,
              accuracy: data.accuracy || 85,
              signal_strength_level: signalStrengthLevel,
              all_agree: signalStrengthLevel === 3,
              indicators: indicators,
              signal_method: data.signal_method,  // Include signal method
              recommended_expiry: data.recommended_expiry,
              expiry: data.expiry,
              reason: data.reason,
              signal_id: data.signal_id,
              timeframe: data.timeframe,
              risk_level: riskLevel,
              av_warnings: data.av_warnings || [],
              // Score based on signal strength AND payout (prioritize high payout)
              score: data.accuracy + (signalStrengthLevel * 20) + ((asset.payout || 85) - 85) * 2
            };
          }
          return null;
        } catch (error) {
          console.error(`Error scanning ${asset.symbol}:`, error);
          return null;
        }
      });
      
      // Wait for all scans to complete
      const results = await Promise.all(scanPromises);
      
      if (isCancelled || !autoScannerEnabled) return;
      
      // Filter valid signals and sort by score (best first)
      const validSignals = results.filter(r => r !== null).sort((a, b) => b.score - a.score);
      
      if (validSignals.length > 0) {
        // Found signals! Take the BEST one
        const best = validSignals[0];
        const isOTC = best.asset.symbol?.includes('_OTC');
        console.log(`✓ Found ${validSignals.length} signals! Best: ${best.asset.name}${isOTC ? ' [OTC]' : ''} (${best.signal}) with ${best.signal_strength_level}/3 indicators, ${best.asset.payout}% payout`);
        
        // Stop scanning and show signal immediately
        setAutoScannerRunning(false);
        setScanComplete(true);
        setAwaitingTradeReport(true);
        setAnalyzeStatus('');
        
        // Debug log for bestSignal
        console.log('🎯 Setting bestSignal:', {
          signal: best.signal,
          signal_method: best.signal_method,
          indicators_method: best.indicators?.method,
          indicators_formation: best.indicators?.formation
        });
        
        setBestSignal({
          asset: best.asset,
          signal: best.signal,
          accuracy: best.accuracy,
          signal_strength_level: best.signal_strength_level,
          all_agree: best.all_agree,
          recommended_expiry: best.recommended_expiry,
          risk_level: best.risk_level,
          av_warnings: best.av_warnings,
          indicators: best.indicators,  // Include indicators for UDU-DUD display
          signal_method: best.signal_method  // Include signal method
        });
        
        setSelectedManualPair(best.asset.symbol);
        
        // Set signal for display
        setLastManualSignalResult({
          type: 'success',
          signal: best.signal,
          signal_id: best.signal_id || `auto_${Date.now()}`,
          accuracy: best.accuracy,
          expiry: best.expiry || '1 min',
          recommended_expiry: best.recommended_expiry || { time: '1m', confidence: 'HIGH', reason: 'Auto Scanner Signal' },
          reason: best.reason || `Best from ${validSignals.length} signals`,
          asset_name: best.asset.name,
          asset_symbol: best.asset.symbol, // Store symbol for OTC detection
          payout: best.asset.payout || 92,
          timeframe: best.timeframe || '1m',
          signal_strength_level: best.signal_strength_level,
          risk_level: best.risk_level,
          indicators: best.indicators,
          signal_method: best.signal_method  // Include signal method for UDU-DUD display
        });
        
        // Set signal info for reporting
        setLastSignalInfo({
          signal_id: best.signal_id || `auto_${Date.now()}`,
          pair: best.asset.symbol,
          symbol: best.asset.symbol, // Add symbol for OTC detection in UI
          asset_name: best.asset.name,
          signal: best.signal
        });
        
        // Start entry countdown
        startEntryCountdown();
        
        // Show toast with OTC label
        const emoji = best.signal === 'BUY' ? '📈' : '📉';
        const assetDisplayName = getAssetNameWithOtc(best.asset.name, best.asset.symbol);
        toast.success(`${emoji} ${best.signal} ${assetDisplayName} - ${best.accuracy}% | 💰${best.asset.payout}%`, { duration: 10000 });
        
      } else {
        // No valid signals - keep scanning silently
        setTimeout(() => {
          if (!isCancelled && autoScannerEnabled && !awaitingTradeReport) {
            setScanRound(prev => prev + 1);
          }
        }, 2000);
      }
    };
    
    // Start scanning when enabled
    if (autoScannerEnabled && guestUser && !awaitingTradeReport && (!coolingUntil || new Date() >= coolingUntil)) {
      scanAllAssets();
    }
    
    return () => {
      isCancelled = true;
      if (scanInterval) clearInterval(scanInterval);
      setAutoScannerRunning(false);
    };
  }, [autoScannerEnabled, guestUser, highPayoutOnly, highPayoutAssets, awaitingTradeReport, scanRound, coolingUntil, blockedLowPayoutAssets, signalMethod, isSubscriptionExpired]);

  // Function to handle trade report and resume scanning
  const handleTradeReported = async (result) => {
    const guestToken = localStorage.getItem('guestToken');
    
    // Handle Low Payout - Block this asset for 15 minutes
    if (result === 'low_payout') {
      const assetSymbol = bestSignal?.asset?.symbol || lastSignalInfo?.pair || lastSignalInfo?.symbol;
      const assetName = bestSignal?.asset?.name || lastSignalInfo?.asset_name || 'Unknown';
      
      if (assetSymbol) {
        const BLOCK_DURATION = 45 * 60 * 1000; // 45 minutes
        const newBlock = {
          symbol: assetSymbol,
          name: assetName,
          blockedAt: Date.now(),
          expiresAt: Date.now() + BLOCK_DURATION
        };
        
        setBlockedLowPayoutAssets(prev => {
          // Remove any existing block for this asset and add new one
          const filtered = prev.filter(item => item.symbol !== assetSymbol);
          const updated = [...filtered, newBlock];
          localStorage.setItem('blockedLowPayoutAssets', JSON.stringify(updated));
          return updated;
        });
        
        toast.warning(`⚠️ ${assetName} blocked for 15 min (low payout)`, { duration: 4000 });
      }
    }
    
    // Report to backend (skip for low_payout as it's client-side only)
    if (guestToken && result !== 'skip' && result !== 'low_payout') {
      try {
        const response = await fetch(`${API}/user/report-trade?token=${guestToken}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            result: result,
            signal_id: lastSignalInfo?.signal_id || null
          })
        });
        
        const data = await response.json();
        if (data.success) {
          toast.success(`Trade result recorded: ${result.toUpperCase()}`);
        } else {
          // If report failed, try to clear pending anyway
          console.warn('Trade report failed:', data.message);
        }
      } catch (err) {
        console.error('Error reporting trade:', err);
        toast.error('Failed to report trade - continuing anyway');
      }
    } else if (result === 'skip') {
      toast.info('Signal skipped - scanning again...');
    }
    
    // Always clear pending_trade_report on backend
    if (guestToken) {
      try {
        await fetch(`${API}/user/clear-pending-report?token=${guestToken}`, {
          method: 'POST'
        });
      } catch (err) {
        // Ignore - we'll continue anyway
      }
    }
    
    // Clear ALL signal state immediately
    setBestSignal(null);
    setAwaitingTradeReport(false);
    setScanComplete(false);
    setScanResults([]);
    setLastManualSignalResult(null);
    setPendingReport(false);
    setLastSignalInfo(null);
    setAvCheckResults(null);
    
    // Clear countdown properly
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (countdownTimeoutRef.current) {
      clearTimeout(countdownTimeoutRef.current);
      countdownTimeoutRef.current = null;
    }
    setEntryCountdown(null);
    
    // Trigger new scan round after a short delay
    setTimeout(() => {
      setScanRound(prev => prev + 1);
    }, 500);
  };

  // Check for referral code in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode && !guestUser) {
      setUrlReferralCode(refCode.toUpperCase());
    }
  }, [guestUser]);

  // Fetch user referral info
  useEffect(() => {
    const fetchReferralInfo = async () => {
      const guestToken = localStorage.getItem('guestToken');
      if (guestToken && guestUser) {
        try {
          const response = await fetch(`${API}/user/referral-info?token=${guestToken}`);
          const data = await response.json();
          if (data.success) {
            setReferralInfo(data);
          }
        } catch (err) {
          // Ignore errors
        }
      }
    };
    
    fetchReferralInfo();
  }, [guestUser]);

  // Fetch promotion on load
  useEffect(() => {
    const fetchPromotion = async () => {
      try {
        const response = await fetch(`${API}/promotion`);
        const data = await response.json();
        if (data.success && data.enabled) {
          setPromotion(data);
        }
      } catch (err) {
        // Ignore promotion fetch errors
      }
    };
    fetchPromotion();
  }, []);

  // Countdown timer for flash sales
  useEffect(() => {
    if (!promotion?.end_time) {
      setPromoCountdown(null);
      return;
    }

    const endTime = new Date(promotion.end_time).getTime();
    
    const updateCountdown = () => {
      const now = Date.now();
      const diff = endTime - now;
      
      if (diff <= 0) {
        setPromoCountdown({ expired: true });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setPromoCountdown({ days, hours, minutes, seconds, expired: false });
    };
    
    // Initial update
    updateCountdown();
    
    // Update every second
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [promotion?.end_time]);

  // Copy discount code to clipboard
  const copyDiscountCode = () => {
    if (promotion?.discount_code) {
      navigator.clipboard.writeText(promotion.discount_code);
      toast.success('Discount code copied to clipboard!');
    }
  };

  // Check authentication on load
  useEffect(() => {
    const checkAuth = async () => {
      setIsCheckingAuth(true);
      
      const guestToken = localStorage.getItem('guestToken');
      if (guestToken) {
        try {
          const response = await fetch(`${API}/guest/verify?token=${guestToken}`);
          const data = await response.json();
          if (data.valid && data.user) {
            setGuestUser(data.user);
            
            // Check if subscription is expired - show renewal modal
            if (data.subscription_expired || data.show_renewal_modal || data.user.subscription_expired) {
              setShowRenewalModal(true);
            }
          } else {
            localStorage.removeItem('guestToken');
            localStorage.removeItem('guestUser');
            localStorage.removeItem('show_renewal_modal');
            if (data.blocked) {
              toast.error('Your account has been blocked. Please contact support.');
            }
          }
        } catch (err) {
          console.log('Guest token invalid');
        }
      }
      
      // Check if we need to show renewal modal from login response
      const showRenewal = localStorage.getItem('show_renewal_modal');
      if (showRenewal === 'true') {
        setShowRenewalModal(true);
        localStorage.removeItem('show_renewal_modal');
      }
      
      setIsCheckingAuth(false);
    };
    
    checkAuth();
  }, []);

  // Show PRO promo to standard users (non-PRO) after they've used the scanner for a bit
  useEffect(() => {
    // Only show promo to logged-in users who don't have PRO access
    if (!guestUser || guestUser.has_pro_access || showProMode) return;
    
    // Check if user has seen the promo recently (don't spam)
    const lastPromoShown = localStorage.getItem('proPromoLastShown');
    const now = Date.now();
    const hoursSinceLastPromo = lastPromoShown ? (now - parseInt(lastPromoShown)) / (1000 * 60 * 60) : 999;
    
    // Only show if they haven't seen it in the last 24 hours
    if (hoursSinceLastPromo < 24) return;
    
    // Show promo after user has reported a few trades (engaged users)
    const tradesReported = parseInt(localStorage.getItem('tradesReportedCount') || '0');
    if (tradesReported >= 3) {
      // Show promo after a delay
      const timer = setTimeout(() => {
        setShowProPromo(true);
        localStorage.setItem('proPromoLastShown', now.toString());
      }, 5000); // 5 second delay
      
      return () => clearTimeout(timer);
    }
  }, [guestUser, showProMode]);

  // Heartbeat for online tracking
  useEffect(() => {
    const sendHeartbeat = async () => {
      const guestToken = localStorage.getItem('guestToken');
      if (guestToken) {
        try {
          await fetch(`${API}/guest/heartbeat?token=${guestToken}`, { method: 'POST' });
        } catch (err) {
          // Ignore heartbeat errors
        }
      }
    };

    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 120000);
    return () => clearInterval(interval);
  }, []);

  // Handle support ticket submission
  const handleSupportTicket = async (e) => {
    e.preventDefault();
    if (!supportName || !supportEmail || !supportSubject || !supportMessage) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSupportLoading(true);
    try {
      const response = await fetch(`${API}/support/ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: supportName,
          email: supportEmail,
          subject: supportSubject,
          message: supportMessage
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setSupportSuccess(true);
        setSupportName('');
        setSupportEmail('');
        setSupportSubject('');
        setSupportMessage('');
        toast.success('Support ticket submitted successfully! We\'ll get back to you soon.');
        setTimeout(() => setSupportSuccess(false), 5000);
      } else {
        toast.error(data.message || 'Failed to submit ticket');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setSupportLoading(false);
    }
  };

  // Fetch coaching packages
  const fetchCoachingPackages = async () => {
    try {
      const response = await fetch(`${API}/coaching/packages`);
      const data = await response.json();
      if (data.success) {
        setCoachingPackages(data.packages);
      }
    } catch (err) {
      console.error('Error fetching coaching packages:', err);
    }
  };

  // Fetch coaching availability for a month
  const fetchCoachingAvailability = async (month, year) => {
    try {
      const response = await fetch(`${API}/coaching/availability?month=${month + 1}&year=${year}`);
      const data = await response.json();
      if (data.success) {
        setCoachingAvailability(data.availability);
      }
    } catch (err) {
      console.error('Error fetching coaching availability:', err);
    }
  };

  // Book coaching session
  const bookCoachingSession = async () => {
    if (!selectedCoachingPackage || !selectedCoachingDate || !selectedCoachingTime) {
      toast.error('Please select a package, date, and time');
      return;
    }

    setCoachingLoading(true);
    try {
      const response = await fetch(`${API}/coaching/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: guestUser?.email || '',
          user_name: guestUser?.name || 'Guest',
          package_type: selectedCoachingPackage,
          date: selectedCoachingDate,
          time_slot: selectedCoachingTime
        })
      });

      const data = await response.json();
      if (data.success) {
        setCoachingBookingId(data.booking_id);
        setCoachingStep('payment');
        toast.success('Time slot reserved! Please complete payment.');
      } else {
        toast.error(data.message || 'Failed to book session');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setCoachingLoading(false);
    }
  };

  // Submit coaching payment proof
  const submitCoachingPaymentProof = async () => {
    if (!coachingPaymentProof || !coachingBookingId) {
      toast.error('Please upload payment proof');
      return;
    }

    setCoachingLoading(true);
    try {
      const response = await fetch(`${API}/coaching/payment-proof`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: coachingBookingId,
          screenshot: coachingPaymentProof.base64,
          filename: coachingPaymentProof.filename
        })
      });

      const data = await response.json();
      if (data.success) {
        setCoachingStep('confirmation');
        toast.success('Payment proof submitted! We\'ll verify and confirm your booking.');
      } else {
        toast.error(data.message || 'Failed to submit payment proof');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setCoachingLoading(false);
    }
  };

  // Handle coaching payment proof file upload
  const handleCoachingFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setCoachingPaymentProof({
        base64: reader.result,
        filename: file.name
      });
    };
    reader.readAsDataURL(file);
  };

  // Reset coaching modal
  const resetCoachingModal = () => {
    setShowCoachingModal(false);
    setSelectedCoachingPackage(null);
    setSelectedCoachingDate(null);
    setSelectedCoachingTime(null);
    setCoachingStep('packages');
    setCoachingBookingId(null);
    setCoachingPaymentProof(null);
  };

  // Fetch user's coaching bookings
  const fetchUserCoachingBookings = async () => {
    const guestToken = localStorage.getItem('guestToken');
    if (!guestToken) return;
    
    setLoadingBookings(true);
    try {
      const response = await fetch(`${API}/user/coaching/bookings?token=${guestToken}`);
      const data = await response.json();
      if (data.success) {
        setUserCoachingBookings(data.bookings || []);
      }
    } catch (err) {
      console.error('Error fetching coaching bookings:', err);
    } finally {
      setLoadingBookings(false);
    }
  };

  // Open coaching modal
  const openCoachingModal = () => {
    setShowCoachingModal(true);
    fetchCoachingPackages();
    fetchCoachingAvailability(viewingMonth, viewingYear);
    fetchUserCoachingBookings(); // Fetch user's bookings
  };

  // Check for pending trade report and fetch profitability
  useEffect(() => {
    const checkPendingAndStats = async () => {
      const guestToken = localStorage.getItem('guestToken');
      if (guestToken && guestUser) {
        try {
          // Check pending report
          const pendingRes = await fetch(`${API}/user/check-pending-report?token=${guestToken}`);
          const pendingData = await pendingRes.json();
          if (pendingData.success) {
            setPendingReport(pendingData.pending);
            setLastSignalInfo(pendingData.last_signal);
          }
          
          // Fetch profitability stats
          const statsRes = await fetch(`${API}/user/profitability?token=${guestToken}`);
          const statsData = await statsRes.json();
          if (statsData.success) {
            setProfitabilityStats(statsData.stats);
          }
        } catch (err) {
          console.log('Error fetching user data');
        }
      }
    };
    
    checkPendingAndStats();
    const interval = setInterval(checkPendingAndStats, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [guestUser]);

  // Fetch education notifications
  useEffect(() => {
    const fetchEducationNotifications = async () => {
      const guestToken = localStorage.getItem('guestToken');
      if (guestToken && guestUser) {
        try {
          const response = await fetch(`${API}/education/notifications?token=${guestToken}`);
          const data = await response.json();
          if (data.success) {
            setEducationNotifications(data.unviewed_count || 0);
          }
        } catch (err) {
          // Ignore errors
        }
      }
    };
    
    fetchEducationNotifications();
    const interval = setInterval(fetchEducationNotifications, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [guestUser]);

  // Fetch available pairs
  useEffect(() => {
    const fetchPairs = async () => {
      try {
        const response = await fetch(`${API}/pairs`);
        const data = await response.json();
        if (data.success && data.pairs) {
          setManualPairs(data.pairs);
        }
      } catch (error) {
        console.error('Error fetching pairs:', error);
      }
    };
    fetchPairs();
  }, []);

  // Fetch top trader performers
  useEffect(() => {
    const fetchTopTraderPerformers = async () => {
      try {
        const response = await fetch(`${API}/public/top-performers`);
        const data = await response.json();
        if (data.success && data.performers) {
          setTopTraderPerformers(data.performers);
        }
      } catch (error) {
        console.error('Error fetching top performers:', error);
      }
    };
    fetchTopTraderPerformers();
    const interval = setInterval(fetchTopTraderPerformers, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  // Fetch top performing assets
  useEffect(() => {
    const fetchTopPerformers = async () => {
      setLoadingTopPerformers(true);
      try {
        const response = await fetch(`${API}/assets/top-performers`);
        const data = await response.json();
        if (data.success) {
          setTopPerformers(data);
        }
      } catch (error) {
        console.error('Error fetching top performers:', error);
      } finally {
        setLoadingTopPerformers(false);
      }
    };
    fetchTopPerformers();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchTopPerformers, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleGuestLoginSuccess = (user, subscriptionExpired) => {
    setGuestUser(user);
    
    // Check if subscription is expired - show renewal modal
    if (subscriptionExpired || user.subscription_expired) {
      setShowRenewalModal(true);
      toast.warning('Your subscription has expired. Please renew to continue using the service.', {
        duration: 5000
      });
      return;
    }
    
    // Check if force_payment is enabled - redirect to payment modal
    if (user.force_payment) {
      toast.warning('Your subscription requires payment. Please complete payment to continue.', {
        duration: 5000
      });
      // Open payment modal
      setShowPaymentModal(true);
      return;
    }
    
    toast.success(`Welcome, ${user.name}!`);
  };

  const handleLogout = async () => {
    const guestToken = localStorage.getItem('guestToken');
    if (guestToken) {
      try {
        await fetch(`${API}/guest/logout?token=${guestToken}`, { method: 'POST' });
      } catch (err) {
        // Ignore
      }
    }
    localStorage.removeItem('guestToken');
    localStorage.removeItem('guestUser');
    setGuestUser(null);
    setPendingReport(false);
    setProfitabilityStats(null);
    toast.success('Logged out successfully');
  };

  // Report trade result
  const reportTradeResult = async (result) => {
    setReportingResult(true);
    const guestToken = localStorage.getItem('guestToken');
    
    // Handle Low Payout - Block this asset for 15 minutes
    if (result === 'low_payout') {
      const assetSymbol = lastSignalInfo?.pair || lastSignalInfo?.symbol || lastManualSignalResult?.asset_symbol || selectedManualPair;
      const assetName = lastSignalInfo?.asset_name || lastManualSignalResult?.asset_name || 'Unknown';
      
      if (assetSymbol) {
        const BLOCK_DURATION = 45 * 60 * 1000; // 45 minutes
        const newBlock = {
          symbol: assetSymbol,
          name: assetName,
          blockedAt: Date.now(),
          expiresAt: Date.now() + BLOCK_DURATION
        };
        
        setBlockedLowPayoutAssets(prev => {
          const filtered = prev.filter(item => item.symbol !== assetSymbol);
          const updated = [...filtered, newBlock];
          localStorage.setItem('blockedLowPayoutAssets', JSON.stringify(updated));
          return updated;
        });
        
        toast.warning(`⚠️ ${assetName} blocked for 15 min (low payout)`, { duration: 4000 });
      }
      
      // Clear the signal state
      setPendingReport(false);
      setLastSignalInfo(null);
      setLastManualSignalResult(null);
      setEntryCountdown(null);
      setReportingResult(false);
      return;
    }
    
    try {
      const response = await fetch(`${API}/user/report-trade?token=${guestToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          result: result,
          signal_id: lastSignalInfo?.signal_id,
          mode: 'standard'  // Identify this as a Standard trade
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Trade result recorded: ${result.toUpperCase()}`);
        setPendingReport(false);
        setLastSignalInfo(null);
        setLastManualSignalResult(null);
        setEntryCountdown(null); // Clear entry countdown
        
        // Increment trade counter for promo trigger
        const currentCount = parseInt(localStorage.getItem('tradesReportedCount') || '0');
        localStorage.setItem('tradesReportedCount', (currentCount + 1).toString());
        
        // Refresh profitability stats
        const statsRes = await fetch(`${API}/user/profitability?token=${guestToken}`);
        const statsData = await statsRes.json();
        if (statsData.success) {
          setProfitabilityStats(statsData.stats);
        }
      } else {
        toast.error(data.message || 'Failed to report result');
      }
    } catch (error) {
      toast.error('Failed to report trade result');
    } finally {
      setReportingResult(false);
    }
  };

  // ============ SMART TRADING SESSION MANAGER ============
  
  /*
   * SMART MONEY MANAGEMENT SYSTEM
   * 
   * 1. PHASE-BASED TRADING:
   *    - Build Phase (0-30% of target): Conservative, build cushion
   *    - Acceleration Phase (30-70%): Moderate compound on wins
   *    - Close-Out Phase (70-100%): Aggressive to finish fast
   *    - Recovery Phase (in drawdown): Smart recovery without martingale
   * 
   * 2. SMART LOT SIZING:
   *    - Base lot from Kelly-inspired formula
   *    - Compound on wins (anti-martingale)
   *    - Smart recovery on losses (recover in 2-3 wins, not 1)
   * 
   * 3. DYNAMIC PROFIT PROTECTION:
   *    - Trailing protection that tightens as profit grows
   *    - Never give back more than a % of peak profit
   */
  
  // Get current trading phase based on progress
  const getTradingPhase = (currentPnL, profitTarget, riskAmount) => {
    const progressPercent = (currentPnL / profitTarget) * 100;
    
    if (currentPnL < 0) {
      return { phase: 'RECOVERY', aggression: 0.8, description: 'Recovery Mode' };
    } else if (progressPercent < 30) {
      return { phase: 'BUILD', aggression: 1.0, description: 'Building Cushion' };
    } else if (progressPercent < 70) {
      return { phase: 'ACCELERATE', aggression: 1.2, description: 'Accelerating' };
    } else {
      return { phase: 'CLOSE_OUT', aggression: 1.4, description: 'Closing Out' };
    }
  };
  
  // Calculate Compound Interest Plan
  const calculateCompoundPlan = () => {
    const { startingBalance, dailyInterest, durationDays } = compoundConfig;
    
    if (startingBalance <= 0 || dailyInterest <= 0 || durationDays <= 0) {
      return;
    }
    
    const days = Math.min(durationDays, 30); // Max 30 days
    const plan = [];
    let currentBalance = parseFloat(startingBalance);
    const dailyRate = parseFloat(dailyInterest) / 100;
    
    for (let day = 1; day <= days; day++) {
      const dailyProfit = currentBalance * dailyRate;
      const endBalance = currentBalance + dailyProfit;
      
      plan.push({
        day,
        startBalance: Math.round(currentBalance * 100) / 100,
        dailyTarget: Math.round(dailyProfit * 100) / 100,
        endBalance: Math.round(endBalance * 100) / 100
      });
      
      currentBalance = endBalance;
    }
    
    const newPlan = {
      config: { ...compoundConfig },
      days: plan,
      totalGrowth: Math.round((currentBalance - startingBalance) * 100) / 100,
      growthPercent: Math.round(((currentBalance / startingBalance) - 1) * 10000) / 100,
      createdAt: new Date().toISOString()
    };
    
    setCompoundPlan(newPlan);
    localStorage.setItem('compoundInterestPlan', JSON.stringify(newPlan));
    localStorage.setItem('compoundInterestConfig', JSON.stringify(compoundConfig));
  };
  
  // Reset compound interest plan
  const resetCompoundPlan = () => {
    setCompoundPlan(null);
    setCompoundChecks({});
    localStorage.removeItem('compoundInterestPlan');
    localStorage.removeItem('compoundInterestChecks');
  };

  // Download the daily compound plan as PDF
  const [downloadingCompoundPdf, setDownloadingCompoundPdf] = useState(false);
  const downloadCompoundPlanPdf = async () => {
    if (!compoundPlan || downloadingCompoundPdf) return;
    setDownloadingCompoundPdf(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      const res = await fetch(`${API}/compound-plan/daily-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          starting_balance: Number(compoundPlan.config?.startingBalance) || 0,
          daily_interest: Number(compoundPlan.config?.dailyInterest) || 0,
          duration_days: Number(compoundPlan.config?.durationDays) || compoundPlan.days.length,
          days: compoundPlan.days,
          total_growth: Number(compoundPlan.totalGrowth) || 0,
          growth_percent: Number(compoundPlan.growthPercent) || 0,
          trader_name: guestUser?.name || guestUser?.email?.split('@')[0] || '',
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `forthewisepro_daily_compound_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Plan downloaded');
    } catch (e) {
      toast.error('Could not generate PDF. Please try again.');
    } finally {
      setDownloadingCompoundPdf(false);
    }
  };
  
  // Toggle day check (achieved/failed)
  const toggleDayCheck = (day, status) => {
    const newChecks = { ...compoundChecks };
    if (newChecks[day] === status) {
      delete newChecks[day]; // Toggle off if clicking same status
    } else {
      newChecks[day] = status; // 'achieved' or 'failed'
    }
    setCompoundChecks(newChecks);
    localStorage.setItem('compoundInterestChecks', JSON.stringify(newChecks));
  };
  
  // Calculate dynamic protection level based on progress
  const calculateProtectionLevel = (currentPnL, peakProfit, profitTarget) => {
    if (peakProfit <= 0) return 0;
    
    const progressPercent = (peakProfit / profitTarget) * 100;
    
    // Protection increases as we get closer to target
    // At 25% of target: protect 40% of peak
    // At 50% of target: protect 55% of peak  
    // At 75% of target: protect 70% of peak
    // At 100%+ of target: protect 85% of peak
    let protectionPercent;
    if (progressPercent < 25) {
      protectionPercent = 0.40;
    } else if (progressPercent < 50) {
      protectionPercent = 0.40 + (progressPercent - 25) * 0.006; // 40% to 55%
    } else if (progressPercent < 75) {
      protectionPercent = 0.55 + (progressPercent - 50) * 0.006; // 55% to 70%
    } else {
      protectionPercent = 0.70 + Math.min(progressPercent - 75, 25) * 0.006; // 70% to 85%
    }
    
    // Protected floor = peak profit * protection percentage
    return Math.round(peakProfit * protectionPercent * 100) / 100;
  };
  
  // Calculate all possible win/loss scenarios for the session
  const calculateWinScenarios = (config) => {
    const { profitTarget, payoutPercent, totalTrades, riskAmount, accountSize } = config;
    const payout = payoutPercent / 100;
    const scenarios = [];
    
    // For each possible number of wins (from minimum viable to all wins)
    for (let wins = 1; wins <= totalTrades; wins++) {
      const losses = totalTrades - wins;
      
      // Formula: (wins × lot × payout) - (losses × lot) = target
      // lot × (wins × payout - losses) = target
      // lot = target / (wins × payout - losses)
      
      const multiplier = (wins * payout) - losses;
      
      // Skip if mathematically impossible (more losses than win profit)
      if (multiplier <= 0) continue;
      
      const requiredLot = profitTarget / multiplier;
      const totalRisk = requiredLot * losses; // Max you could lose
      const profitPerWin = requiredLot * payout;
      
      // Check if this scenario is viable within risk constraints
      const isViable = totalRisk <= riskAmount && requiredLot <= accountSize * 0.15;
      const isRecommended = wins >= Math.ceil(totalTrades * 0.5) && totalRisk <= riskAmount * 0.8;
      
      scenarios.push({
        wins,
        losses,
        requiredLot: Math.round(requiredLot * 100) / 100,
        profitPerWin: Math.round(profitPerWin * 100) / 100,
        totalRisk: Math.round(totalRisk * 100) / 100,
        winRate: Math.round((wins / totalTrades) * 100),
        isViable,
        isRecommended
      });
    }
    
    return scenarios;
  };
  
  // Calculate initial lot size with smarter formula
  const calculateSessionPlan = (config, selectedTargetWins = null) => {
    const { accountSize, riskAmount, profitTarget, payoutPercent, totalTrades } = config;
    const effectivePayout = payoutPercent / 100;
    
    // If user selected specific target wins, use that
    if (selectedTargetWins) {
      const losses = totalTrades - selectedTargetWins;
      const multiplier = (selectedTargetWins * effectivePayout) - losses;
      
      if (multiplier > 0) {
        const lotForTarget = profitTarget / multiplier;
        // Apply safety caps
        const safeLot = Math.min(lotForTarget, riskAmount / losses, accountSize * 0.12);
        const profitPerWin = safeLot * effectivePayout;
        
        return {
          baseLotSize: Math.round(safeLot * 100) / 100,
          requiredWins: selectedTargetWins,
          profitPerWin: Math.round(profitPerWin * 100) / 100,
          maxLoss: riskAmount,
          targetWins: selectedTargetWins,
          expectedLosses: losses,
          maxRisk: Math.round(safeLot * losses * 100) / 100
        };
      }
    }
    
    // Auto-calculate: Goal is to reach target in ~60% of allowed trades
    const autoTargetWins = Math.ceil(totalTrades * 0.6);
    const autoLosses = totalTrades - autoTargetWins;
    const multiplier = (autoTargetWins * effectivePayout) - autoLosses;
    
    let baseLotSize;
    if (multiplier > 0) {
      const idealLotForTarget = profitTarget / multiplier;
      // Risk-based maximum lot
      const maxLotFromRisk = riskAmount / Math.max(autoLosses, 1);
      // Account-based maximum (never more than 10% of account)
      const maxLotFromAccount = accountSize * 0.10;
      // Take the minimum of all constraints
      baseLotSize = Math.min(idealLotForTarget, maxLotFromRisk, maxLotFromAccount);
    } else {
      // Fallback if calculation doesn't work
      baseLotSize = Math.min(riskAmount / totalTrades, accountSize * 0.05);
    }
    
    // Calculate expected performance
    const profitPerWin = baseLotSize * effectivePayout;
    const requiredWins = Math.ceil(profitTarget / profitPerWin);
    
    return {
      baseLotSize: Math.round(baseLotSize * 100) / 100,
      requiredWins: Math.min(requiredWins, totalTrades),
      profitPerWin: Math.round(profitPerWin * 100) / 100,
      maxLoss: riskAmount,
      targetWins: targetWins
    };
  };
  
  // SMART LOT SIZING - The brain of the system
  const calculateNextLotSize = (stats, config, isWin, previousLotSize, winStreak = 0, lossStreak = 0) => {
    const { riskAmount, profitTarget, payoutPercent } = config;
    const { currentPnL, peakProfit } = stats;
    const effectivePayout = payoutPercent / 100;
    
    const remainingToTarget = profitTarget - currentPnL;
    const plan = calculateSessionPlan(config);
    const baseLot = plan.baseLotSize;
    
    // Check if we should stop
    if (remainingToTarget <= 0) return 0; // Target reached
    
    // Get current phase
    const phase = getTradingPhase(currentPnL, profitTarget, riskAmount);
    
    // Calculate available risk (how much we can lose)
    let availableRisk;
    if (currentPnL > 0) {
      // In profit: available risk = current P&L minus protection floor
      const protectionFloor = calculateProtectionLevel(currentPnL, peakProfit || currentPnL, profitTarget);
      availableRisk = currentPnL - protectionFloor;
    } else {
      // In loss: available risk = remaining before hitting stop
      availableRisk = riskAmount + currentPnL;
    }
    
    if (availableRisk <= 0) return 0; // Stop reached
    
    let nextLot;
    
    if (phase.phase === 'RECOVERY') {
      // ===== SMART RECOVERY =====
      // Don't martingale! Instead, size to recover loss in 2-3 wins
      const amountToRecover = Math.abs(currentPnL);
      const winsToRecover = 3; // Aim to recover in 3 wins
      const recoveryLot = amountToRecover / (winsToRecover * effectivePayout);
      
      // Cap recovery lot at 1.3x base (no aggressive martingale)
      nextLot = Math.min(recoveryLot, baseLot * 1.3);
      
      // But never risk more than 40% of remaining risk budget
      nextLot = Math.min(nextLot, availableRisk * 0.4);
      
      // Reduce further on loss streaks
      if (lossStreak >= 2) {
        nextLot = nextLot * 0.85; // Reduce by 15% on 2+ loss streak
      }
      
    } else if (isWin) {
      // ===== WINNING - SMART COMPOUND =====
      const lastProfit = previousLotSize * effectivePayout;
      
      // Compound percentage based on phase
      let compoundPercent;
      switch (phase.phase) {
        case 'BUILD':
          compoundPercent = 0.3; // Conservative: add 30% of last profit
          break;
        case 'ACCELERATE':
          compoundPercent = 0.5; // Moderate: add 50% of last profit
          break;
        case 'CLOSE_OUT':
          compoundPercent = 0.7; // Aggressive: add 70% of last profit to close faster
          break;
        default:
          compoundPercent = 0.3;
      }
      
      // Win streak bonus (max 20% extra)
      const streakBonus = Math.min(winStreak * 0.05, 0.2);
      compoundPercent += streakBonus;
      
      // Calculate compound amount
      const compoundAmount = lastProfit * compoundPercent;
      nextLot = baseLot + compoundAmount;
      
      // Phase-based maximum multiplier
      const maxMultiplier = phase.phase === 'CLOSE_OUT' ? 2.5 : 
                           phase.phase === 'ACCELERATE' ? 2.0 : 1.5;
      nextLot = Math.min(nextLot, baseLot * maxMultiplier);
      
      // Close-out optimization: if we can hit target in 1-2 more wins, size up
      if (phase.phase === 'CLOSE_OUT' && remainingToTarget <= baseLot * effectivePayout * 2) {
        const closeOutLot = remainingToTarget / effectivePayout;
        // Only if it's within our risk tolerance
        if (closeOutLot <= availableRisk * 0.6) {
          nextLot = Math.max(nextLot, closeOutLot);
        }
      }
      
      // Safety cap: never risk more than 50% of available risk on a single trade
      nextLot = Math.min(nextLot, availableRisk * 0.5);
      
    } else {
      // ===== AFTER LOSS (but not in deep recovery) =====
      // Reduce slightly but don't collapse
      nextLot = baseLot * 0.9;
      
      // Further reduce on loss streaks
      if (lossStreak >= 2) {
        nextLot = baseLot * 0.8;
      }
      
      // Cap at 40% of available risk after a loss
      nextLot = Math.min(nextLot, availableRisk * 0.4);
    }
    
    // Absolute minimum $1
    nextLot = Math.max(nextLot, 1);
    
    // Round to 2 decimal places
    return Math.round(nextLot * 100) / 100;
  };
  
  // Start a new trading session
  const startTradingSession = () => {
    // Save config to localStorage
    localStorage.setItem('tradingSessionConfig', JSON.stringify(sessionConfig));
    
    // Use selected target wins or auto-calculate
    const plan = calculateSessionPlan(sessionConfig, targetWins);
    
    setSessionTrades([]);
    setSessionStats({
      currentPnL: 0,
      wins: 0,
      losses: 0,
      currentLotSize: plan.baseLotSize,
      tradesRemaining: sessionConfig.totalTrades,
      requiredWins: plan.requiredWins,
      targetReached: false,
      stopReached: false,
      peakProfit: 0,
      protectedFloor: 0,
      winStreak: 0,
      lossStreak: 0,
      phase: 'BUILD',
      targetWinsNeeded: plan.requiredWins,
      allowedLosses: sessionConfig.totalTrades - plan.requiredWins
    });
    setSessionActive(true);
    setShowSessionSetup(false);
    setCoolingUntil(null);
    
    const lossesAllowed = sessionConfig.totalTrades - plan.requiredWins;
    toast.success(`Session started! Need ${plan.requiredWins} wins (can lose ${lossesAllowed}) | Lot: $${plan.baseLotSize}`);
  };
  
  // Report trade result for session - SMART SYSTEM + LINKED TO ACTUAL TRADE REPORT
  const reportSessionTrade = async (result) => {
    const isWin = result === 'win';
    const { currentLotSize, currentPnL, wins, losses, tradesRemaining, peakProfit, winStreak, lossStreak } = sessionStats;
    const effectivePayout = sessionConfig.payoutPercent / 100;
    const guestToken = localStorage.getItem('guestToken');
    
    // ===== LINK TO ACTUAL TRADE REPORT SYSTEM =====
    // If there's a pending signal from the scanner, report it to the main system too
    if (lastSignalInfo || bestSignal) {
      try {
        const signalId = lastSignalInfo?.signal_id || bestSignal?.signal_id;
        if (signalId) {
          const response = await fetch(`${API}/user/report-trade?token=${guestToken}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              result: result,
              signal_id: signalId
            })
          });
          
          const data = await response.json();
          if (data.success) {
            // Clear the pending signal
            setPendingReport(false);
            setLastSignalInfo(null);
            setLastManualSignalResult(null);
            setBestSignal(null);
            setAwaitingTradeReport(false);
            setEntryCountdown(null);
            
            // Refresh profitability stats
            const statsRes = await fetch(`${API}/user/profitability?token=${guestToken}`);
            const statsData = await statsRes.json();
            if (statsData.success) {
              setProfitabilityStats(statsData.stats);
            }
          }
        }
      } catch (error) {
        console.error('Failed to report to main trade system:', error);
      }
    }
    
    // ===== SESSION MANAGER LOGIC =====
    // Calculate P&L change
    const pnlChange = isWin ? (currentLotSize * effectivePayout) : -currentLotSize;
    const newPnL = Math.round((currentPnL + pnlChange) * 100) / 100;
    const newWins = isWin ? wins + 1 : wins;
    const newLosses = isWin ? losses : losses + 1;
    const newTradesRemaining = tradesRemaining - 1;
    
    // Update streaks
    const newWinStreak = isWin ? (winStreak + 1) : 0;
    const newLossStreak = isWin ? 0 : (lossStreak + 1);
    
    // Update peak profit tracking
    const newPeakProfit = Math.max(peakProfit, newPnL);
    
    // Calculate DYNAMIC protected floor based on progress
    const newProtectedFloor = calculateProtectionLevel(newPnL, newPeakProfit, sessionConfig.profitTarget);
    
    // Get current phase
    const phase = getTradingPhase(newPnL, sessionConfig.profitTarget, sessionConfig.riskAmount);
    
    // Check session end conditions
    const targetReached = newPnL >= sessionConfig.profitTarget;
    
    // Stop conditions:
    // 1. Initial stop: went below -riskAmount
    // 2. Profit protection stop: dropped below dynamic protected floor
    const initialStopHit = newPnL <= -sessionConfig.riskAmount;
    const profitProtectionStopHit = newProtectedFloor > 0 && newPnL <= newProtectedFloor;
    const stopReached = initialStopHit || profitProtectionStopHit;
    
    // Calculate next lot size with SMART system
    const nextLotSize = calculateNextLotSize(
      { currentPnL: newPnL, peakProfit: newPeakProfit },
      sessionConfig,
      isWin,
      currentLotSize,
      newWinStreak,
      newLossStreak
    );
    
    // Record the trade
    const trade = {
      tradeNumber: sessionConfig.totalTrades - tradesRemaining + 1,
      lotSize: currentLotSize,
      result: result,
      pnlChange: pnlChange,
      runningPnL: newPnL,
      peakProfit: newPeakProfit,
      protectedFloor: newProtectedFloor,
      phase: phase.phase,
      timestamp: new Date().toISOString()
    };
    
    setSessionTrades(prev => [...prev, trade]);
    
    // Update stats
    // Note: tradesRemaining can go negative - it's a guide, not a hard limit
    setSessionStats({
      currentPnL: newPnL,
      wins: newWins,
      losses: newLosses,
      currentLotSize: nextLotSize,
      tradesRemaining: newTradesRemaining, // Can be negative if continuing past plan
      requiredWins: Math.max(0, Math.ceil((sessionConfig.profitTarget - newPnL) / (nextLotSize * effectivePayout))),
      targetReached,
      stopReached,
      peakProfit: newPeakProfit,
      protectedFloor: newProtectedFloor,
      winStreak: newWinStreak,
      lossStreak: newLossStreak,
      phase: phase.phase,
      remainingToTarget: Math.max(0, sessionConfig.profitTarget - newPnL)
    });
    
    // Handle session end conditions
    // Priority: Target > Stop Loss > Profit Protection > 3 Consecutive Losses (when profitable) > (trades exhausted is just info, not end)
    
    // Check for 3 consecutive losses while in profit - trigger cooldown
    const threeConsecutiveLossesWhileProfitable = newLossStreak >= 3 && newPnL > 0;
    
    if (targetReached) {
      toast.success(`🎉 TARGET HIT! Profit: $${newPnL.toFixed(2)} 🎯`);
      endSession('target');
    } else if (initialStopHit) {
      toast.error(`⚠️ Stop Loss Hit. Loss: $${Math.abs(newPnL).toFixed(2)}`);
      endSession('stop');
    } else if (profitProtectionStopHit) {
      toast.warning(`🛡️ Profit Protected! Locked: $${newPnL.toFixed(2)} (Peak was $${newPeakProfit.toFixed(2)})`);
      endSession('profit_protection');
    } else if (threeConsecutiveLossesWhileProfitable) {
      toast.warning(`🛑 3 Consecutive Losses! Protecting profit: $${newPnL.toFixed(2)}. Cooldown activated.`);
      endSession('three_consecutive_losses');
    } else if (newTradesRemaining <= 0) {
      // Planned trades exhausted but target not hit
      // Calculate how many more wins needed
      const remainingToTarget = sessionConfig.profitTarget - newPnL;
      const winsStillNeeded = Math.ceil(remainingToTarget / (nextLotSize * effectivePayout));
      
      if (remainingToTarget > 0 && nextLotSize > 0) {
        // Target not hit but we can continue - show guidance
        toast.warning(`📊 Planned trades done! P&L: $${newPnL.toFixed(2)} | Need ${winsStillNeeded} more wins to hit target. Keep trading!`);
        // Don't end session - let them continue manually
      } else if (newPnL > 0) {
        // In profit but below target and no lot size (shouldn't happen)
        toast.info(`Session Complete. Profit: $${newPnL.toFixed(2)} (Target was $${sessionConfig.profitTarget})`);
        endSession('trades_exhausted_profit');
      } else {
        toast.info(`Session Complete. Final P&L: $${newPnL.toFixed(2)}`);
        endSession('trades_exhausted');
      }
    } else {
      // Show smart feedback with phase info and progress
      const emoji = isWin ? '✅' : '❌';
      const streakInfo = newWinStreak >= 2 ? `🔥${newWinStreak}` : newLossStreak >= 2 ? `❄️${newLossStreak}` : '';
      const progressPercent = Math.round((newPnL / sessionConfig.profitTarget) * 100);
      
      toast.info(`${emoji} ${phase.description} | $${newPnL.toFixed(2)} (${progressPercent}%) | Next: $${nextLotSize} ${streakInfo}`);
      
      // ===== RESTART SCANNER FOR NEXT TRADE =====
      // If auto scanner is enabled, trigger a new scan round
      if (autoScannerEnabled && guestToken) {
        // Small delay to let the UI update first
        setTimeout(() => {
          setScanRound(prev => prev + 1); // This triggers the scanner useEffect
        }, 500);
      }
    }
  };
  
  // End session and start cooling period
  const endSession = (reason) => {
    setSessionActive(false);
    
    // Start cooling period - stop scanner
    if (autoScannerRunning) {
      setAutoScannerRunning(false);
      toast.info('Scanner stopped for cooling period');
    }
    
    const coolingEnd = new Date(Date.now() + sessionConfig.coolingPeriod * 60 * 1000);
    setCoolingUntil(coolingEnd);
    setCoolingReason(reason);
    // Persist cooling period and reason to localStorage
    localStorage.setItem('tradingCoolingUntil', coolingEnd.toISOString());
    localStorage.setItem('tradingCoolingReason', reason);
    
    // Save session history
    const sessionHistory = JSON.parse(localStorage.getItem('sessionHistory') || '[]');
    sessionHistory.push({
      sessionNumber: sessionConfig.sessionNumber,
      config: sessionConfig,
      trades: sessionTrades,
      finalStats: sessionStats,
      reason,
      endTime: new Date().toISOString()
    });
    localStorage.setItem('sessionHistory', JSON.stringify(sessionHistory.slice(-20))); // Keep last 20 sessions
    
    // Update daily stats
    setDailyStats(prev => {
      const updated = {
        ...prev,
        date: new Date().toDateString(),
        totalPnL: prev.totalPnL + sessionStats.currentPnL,
        totalWins: prev.totalWins + sessionStats.wins,
        totalLosses: prev.totalLosses + sessionStats.losses,
        sessionsCompleted: prev.sessionsCompleted + 1,
        sessionResults: [...prev.sessionResults, {
          sessionNumber: sessionConfig.sessionNumber,
          pnl: sessionStats.currentPnL,
          wins: sessionStats.wins,
          losses: sessionStats.losses,
          reason
        }]
      };
      localStorage.setItem('dailyTradingStats', JSON.stringify(updated));
      return updated;
    });
  };
  
  // Reset session (manual) - just ends current session
  const resetSession = () => {
    // If session was active, save its stats before resetting
    if (sessionActive && (sessionStats.wins > 0 || sessionStats.losses > 0)) {
      setDailyStats(prev => {
        const updated = {
          ...prev,
          date: new Date().toDateString(),
          totalPnL: prev.totalPnL + sessionStats.currentPnL,
          totalWins: prev.totalWins + sessionStats.wins,
          totalLosses: prev.totalLosses + sessionStats.losses,
          sessionsCompleted: prev.sessionsCompleted + 1,
          sessionResults: [...prev.sessionResults, {
            sessionNumber: sessionConfig.sessionNumber,
            pnl: sessionStats.currentPnL,
            wins: sessionStats.wins,
            losses: sessionStats.losses,
            reason: 'manual_end'
          }]
        };
        localStorage.setItem('dailyTradingStats', JSON.stringify(updated));
        return updated;
      });
    }
    
    setSessionActive(false);
    setSessionTrades([]);
    setSessionStats({
      currentPnL: 0,
      wins: 0,
      losses: 0,
      currentLotSize: 0,
      tradesRemaining: 0,
      requiredWins: 0,
      targetReached: false,
      stopReached: false
    });
    setShowSessionSetup(true);
    setCoolingUntil(null);
  };
  
  // Reset daily stats - clears everything
  const resetDailyStats = () => {
    if (!window.confirm('Are you sure you want to reset all daily stats to 0? This cannot be undone.')) {
      return;
    }
    
    const freshStats = {
      date: new Date().toDateString(),
      totalPnL: 0,
      totalWins: 0,
      totalLosses: 0,
      sessionsCompleted: 0,
      sessionResults: []
    };
    setDailyStats(freshStats);
    localStorage.setItem('dailyTradingStats', JSON.stringify(freshStats));
    
    // Also reset current session if active
    setSessionActive(false);
    setSessionTrades([]);
    setSessionStats({
      currentPnL: 0,
      wins: 0,
      losses: 0,
      currentLotSize: 0,
      tradesRemaining: 0,
      requiredWins: 0,
      targetReached: false,
      stopReached: false
    });
    setShowSessionSetup(true);
    // NOTE: Do NOT reset coolingUntil - cooling period should only expire naturally
    
    // Reset session number to 1
    setSessionConfig(prev => ({...prev, sessionNumber: 1}));
    
    toast.success('Daily stats reset to 0' + (coolingUntil ? ' (cooling period still active)' : ''));
  };
  
  // Check cooling period
  useEffect(() => {
    if (!coolingUntil) return;
    
    const checkCooling = setInterval(() => {
      if (new Date() >= coolingUntil) {
        setCoolingUntil(null);
        localStorage.removeItem('tradingCoolingUntil'); // Clear from localStorage
        toast.success('Cooling period ended. You can start a new session!');
        setCoolingReason(null);
        localStorage.removeItem('tradingCoolingReason');
        setShowSessionSetup(true);
      }
    }, 1000);
    
    return () => clearInterval(checkCooling);
  }, [coolingUntil]);

  // ============ END TRADING SESSION MANAGER ============

  const skipSignal = async () => {
    setReportingResult(true);
    const guestToken = localStorage.getItem('guestToken');
    
    try {
      const response = await fetch(`${API}/user/skip-signal?token=${guestToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.info('Signal skipped - generate a new one!');
        setPendingReport(false);
        setLastSignalInfo(null);
        setLastManualSignalResult(null);
        setEntryCountdown(null); // Clear entry countdown
      } else {
        toast.error(data.message || 'Failed to skip signal');
      }
    } catch (error) {
      toast.error('Failed to skip signal');
    } finally {
      setReportingResult(false);
    }
  };

  const generateManualSignal = async () => {
    if (!guestUser) {
      setShowAuthModal(true);
      return;
    }
    
    // Block if subscription expired
    if (isSubscriptionExpired) {
      toast.error('Your subscription has expired. Please renew to generate signals.');
      setShowRenewalModal(true);
      return;
    }
    
    // Check if in cooling period - block signal generation
    if (coolingUntil && new Date() < coolingUntil) {
      const remainingMinutes = Math.ceil((coolingUntil - new Date()) / 60000);
      toast.error(`🚫 Cooling period active! ${remainingMinutes} min remaining. Wait for cooldown to end.`);
      return;
    }
    
    // Check if there's a pending report
    if (pendingReport) {
      toast.error('Please report your previous trade result before generating a new signal');
      return;
    }

    // Start 15-second analyzing phase
    setAnalyzingSignal(true);
    setAnalyzeProgress(0);
    setAnalyzeStatus('');
    setLastManualSignalResult(null);
    
    // Progress animation over 15 seconds
    const progressInterval = setInterval(() => {
      setAnalyzeProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + (100 / 150); // 15 seconds = 150 intervals of 100ms
      });
    }, 100);

    // Wait 15 seconds while "analyzing"
    await new Promise(resolve => setTimeout(resolve, 15000));
    
    clearInterval(progressInterval);
    setAnalyzeProgress(100);
    
    // Show method-specific status message
    const isUduDud = signalMethod === 'udu-dud';
    if (isUduDud) {
      setAnalyzeStatus('🕯️ Looking for UDU-DUD pattern...');
    } else {
      setAnalyzeStatus('✅ Signal Ready! Confirming with MA7...');
    }
    
    const guestToken = localStorage.getItem('guestToken');
    
    // First API call to check if MA7 already agrees
    let ma7Confirmed = false;
    let signalData = null;
    let ma7CountdownSeconds = 10; // Max 10 seconds for MA7 confirmation
    
    try {
      const initialResponse = await fetch(`${API}/scanner/manual-signal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          symbol: selectedManualPair,
          token: guestToken,
          indicators: selectedIndicators,
          anti_volatility: antiVolatilityEnabled,
          timeframe: signalTimeframe,
          method: signalMethod  // 'indicators' or 'udu-dud'
        })
      });
      
      const initialData = await initialResponse.json();
      
      // Check if pending report is required
      if (initialData.pending_report) {
        setPendingReport(true);
        setLastSignalInfo(initialData.last_signal);
        toast.error(initialData.message);
        setAnalyzingSignal(false);
        setAnalyzeStatus('');
        return;
      }
      
      // Check if MA7 already confirmed during first 15 seconds
      if (initialData.success && initialData.signal && initialData.ma7_rsi_stable) {
        ma7Confirmed = true;
        signalData = initialData;
        ma7CountdownSeconds = 0; // No extra wait needed
      } else if (initialData.success && initialData.signal) {
        signalData = initialData;
        // MA7 not yet stable - need to wait and re-check
      }
      
      // MA7 Confirmation countdown (only if not already confirmed)
      if (!ma7Confirmed && signalData) {
        const isUduDud = signalMethod === 'udu-dud';
        for (let i = ma7CountdownSeconds; i > 0; i--) {
          if (isUduDud) {
            setAnalyzeStatus(`🕯️ Confirming pattern... ${i}s`);
          } else {
            setAnalyzeStatus(`✅ Signal Ready! MA7 confirming... ${i}s`);
          }
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Re-check MA7 every 2 seconds
          if (i % 2 === 0) {
            try {
              const checkResponse = await fetch(`${API}/scanner/manual-signal`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                  symbol: selectedManualPair,
                  token: guestToken,
                  indicators: selectedIndicators,
                  anti_volatility: antiVolatilityEnabled,
                  timeframe: signalTimeframe,
                  method: signalMethod  // 'indicators' or 'udu-dud'
                })
              });
              const checkData = await checkResponse.json();
              
              if (checkData.success && checkData.signal && checkData.ma7_rsi_stable) {
                ma7Confirmed = true;
                signalData = checkData;
                if (isUduDud) {
                  const formation = checkData.indicators?.formation || '';
                  setAnalyzeStatus(`🕯️ ${formation} Pattern Confirmed!`);
                } else {
                  setAnalyzeStatus('✅ MA7 Confirmed!');
                }
                await new Promise(resolve => setTimeout(resolve, 500));
                break;
              }
            } catch (e) {
              // Continue countdown even if check fails
            }
          }
        }
      }
      
      // Process the signal (release even if MA7 didn't confirm after 10s)
      if (signalData && signalData.success && signalData.signal) {
        if (!ma7Confirmed) {
          toast.warning('⚠️ MA7 not fully aligned - proceed with caution');
        }
        
        // Store AV check results for display
        if (signalData.av_checks) {
          setAvCheckResults(signalData.av_checks);
        }
        
        processSignal(signalData);
      } else if (signalData && signalData.signal_found === false) {
        // Store AV check results even when blocked
        if (signalData.av_checks) {
          setAvCheckResults(signalData.av_checks);
        }
        
        // Check if blocked by anti-volatility
        if (signalData.blocked_by_av) {
          const blockedReasons = signalData.av_blocked_reasons || [];
          setLastManualSignalResult({
            type: 'blocked',
            message: blockedReasons.join(' | ') || 'Signal blocked by Anti-Volatility Protection',
            av_checks: signalData.av_checks
          });
          toast.warning(`🛡️ Signal blocked: ${blockedReasons[0] || 'Market conditions unsafe'}`);
        } else {
          setLastManualSignalResult({
            type: 'info',
            message: signalData.message || 'No signal detected - try another pair'
          });
          toast.info(signalData.message || 'No signal detected');
        }
      } else {
        setLastManualSignalResult({
          type: 'error',
          message: 'Failed to generate signal'
        });
        toast.error('Failed to generate signal');
      }
    } catch (error) {
      setLastManualSignalResult({
        type: 'error',
        message: 'Connection error'
      });
      toast.error('Failed to connect to server');
    } finally {
      setAnalyzingSignal(false);
      setAnalyzeStatus('');
      setGeneratingSignal(false);
    }
  };
  
  // Helper function to process signal
  const processSignal = (data) => {
    setGeneratingSignal(true);
    setLastManualSignalResult({
      type: 'success',
      signal: data.signal,
      signal_id: data.signal_id,
      accuracy: data.accuracy,
      expiry: data.expiry,
      recommended_expiry: data.recommended_expiry || null,
      reason: data.reason,
      asset_name: data.asset_name,
      asset_symbol: selectedManualPair, // Store symbol for OTC check
      payout: data.payout,
      execute_time_est: data.execute_time_est,
      timeframe: data.timeframe,
      signal_strength_level: data.signal_strength_level || 1,
      indicators: data.indicators || {},
      risk_level: data.risk_level,
      av_warnings: data.av_warnings || []
    });
    
    // Set pending report for the new signal
    setPendingReport(true);
    setLastSignalInfo({
      signal_id: data.signal_id,
      symbol: selectedManualPair,
      asset_name: data.asset_name,
      signal: data.signal
    });
    
    // Start countdown to next 1-minute candle entry
    startEntryCountdown();
    
    // Get asset name with OTC label for toast
    const assetDisplay = getAssetNameWithOtc(data.asset_name, selectedManualPair);
    
    // Check if using UDU-DUD method
    const isUduDud = data.signal_method === 'udu-dud' || data.indicators?.method === 'UDU-DUD';
    
    if (isUduDud) {
      // UDU-DUD specific announcements
      const formation = data.indicators?.formation || 'N/A';
      const expectedNext = data.indicators?.expected_next || '';
      const retracement = data.indicators?.retracement || 0;
      
      if (data.signal === 'CALL') {
        toast.success(
          <div>
            <p className="font-bold">🕯️ {formation} Formation Detected!</p>
            <p>Expecting {expectedNext} = CALL ({data.accuracy}%)</p>
            <p className="text-xs opacity-80">Retracement: {retracement}%</p>
          </div>,
          { duration: 5000 }
        );
      } else if (data.signal === 'PUT') {
        toast.error(
          <div>
            <p className="font-bold">🕯️ {formation} Formation Detected!</p>
            <p>Expecting {expectedNext} = PUT ({data.accuracy}%)</p>
            <p className="text-xs opacity-80">Retracement: {retracement}%</p>
          </div>,
          { duration: 5000 }
        );
      }
    } else {
      // Traditional indicators announcements
      if (data.risk_level === 'SAFE' && data.signal_strength_level === 3) {
        toast.success(`🔥🛡️ PERFECT ${data.signal}! All checks passed + 3 indicators agree! (${data.accuracy}%)`);
      } else if (data.signal_strength_level === 3) {
        toast.success(`🔥🔥🔥 HOT ${data.signal}! All 3 indicators agree! (${data.accuracy}%)`);
      } else if (data.signal_strength_level === 2) {
        toast.success(`💪 STRONG ${data.signal} ${assetDisplay} - 2 indicators agree! (${data.accuracy}%)`);
      } else {
        if (data.signal === 'BUY' || data.signal === 'CALL') {
          toast.success(`🟢 ${data.signal} ${assetDisplay} (${data.accuracy}%)`);
        } else {
          toast.error(`🔴 ${data.signal} ${assetDisplay} (${data.accuracy}%)`);
        }
      }
    }
    
    // Show warnings if any
    if (data.av_warnings && data.av_warnings.length > 0) {
      setTimeout(() => {
        toast.warning(`⚠️ ${data.av_warnings[0]}`);
      }, 1000);
    }
  };
  
  // Start countdown to next 1-minute candle entry
  const startEntryCountdown = () => {
    // Clear any existing countdown first
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (countdownTimeoutRef.current) {
      clearTimeout(countdownTimeoutRef.current);
      countdownTimeoutRef.current = null;
    }
    
    const now = new Date();
    const secondsUntilNextMinute = 60 - now.getSeconds();
    const initialCountdown = secondsUntilNextMinute > 0 ? secondsUntilNextMinute : 60;
    setEntryCountdown(initialCountdown);
    
    console.log(`⏱️ Entry countdown started: ${initialCountdown}s until next 1m candle`);
    
    // Countdown interval - run every second
    let currentCount = initialCountdown;
    countdownIntervalRef.current = setInterval(() => {
      currentCount -= 1;
      
      if (currentCount <= 0) {
        // Countdown complete
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        setEntryCountdown(0);
        toast.info('⏰ NOW! Enter the trade!', { duration: 5000 });
        console.log('⏱️ Entry countdown complete - ENTER NOW!');
      } else {
        setEntryCountdown(currentCount);
      }
    }, 1000);
    
    // Clear countdown after 2 minutes (in case user doesn't report)
    countdownTimeoutRef.current = setTimeout(() => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setEntryCountdown(null);
      console.log('⏱️ Entry countdown expired (2 min timeout)');
    }, 120000);
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Show Pro Scalper Bot if Pro mode is enabled and user has access
  if (showProMode && guestUser?.has_pro_access) {
    return (
      <ProScalperBot 
        user={guestUser}
        onSwitchToStandard={() => {
          setShowProMode(false);
          localStorage.setItem('showProMode', 'false');
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
      <Toaster position="top-right" richColors />
      
      {/* Referral Welcome Banner - Show when visiting with ref code */}
      {urlReferralCode && !guestUser && (
        <div className="max-w-2xl mx-auto mb-4 p-3 rounded-lg border bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/30">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-400" />
              <span className="font-medium text-purple-300">
                {t('youveBeenReferred')}
              </span>
            </div>
            <Button
              onClick={() => setShowAuthModal(true)}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <UserPlus className="w-4 h-4 mr-1" />
              {t('signUpNow')}
            </Button>
          </div>
        </div>
      )}
      
      {/* Promotion Banner - Always visible */}
      {promotion && promotion.enabled && (
        <div className={`max-w-2xl mx-auto mb-4 p-3 rounded-lg border ${
          promotion.banner_color === 'green' ? 'bg-green-500/20 border-green-500/30' :
          promotion.banner_color === 'blue' ? 'bg-blue-500/20 border-blue-500/30' :
          promotion.banner_color === 'red' ? 'bg-red-500/20 border-red-500/30' :
          promotion.banner_color === 'purple' ? 'bg-purple-500/20 border-purple-500/30' :
          'bg-yellow-500/20 border-yellow-500/30'
        }`}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className={`w-5 h-5 ${
                promotion.banner_color === 'green' ? 'text-green-400' :
                promotion.banner_color === 'blue' ? 'text-blue-400' :
                promotion.banner_color === 'red' ? 'text-red-400' :
                promotion.banner_color === 'purple' ? 'text-purple-400' :
                'text-yellow-400'
              }`} />
              <span className={`font-medium ${
                promotion.banner_color === 'green' ? 'text-green-300' :
                promotion.banner_color === 'blue' ? 'text-blue-300' :
                promotion.banner_color === 'red' ? 'text-red-300' :
                promotion.banner_color === 'purple' ? 'text-purple-300' :
                'text-yellow-300'
              }`}>
                {promotion.discount_text || 'Special Offer!'}
              </span>
              
              {/* Flash Sale Countdown Timer */}
              {promoCountdown && !promoCountdown.expired && (
                <div className="flex items-center gap-1 ml-2 px-2 py-1 bg-black/30 rounded-lg">
                  <Clock className="w-3 h-3 text-red-400" />
                  <span className="text-xs font-mono font-bold text-white">
                    {promoCountdown.days > 0 && `${promoCountdown.days}d `}
                    {String(promoCountdown.hours).padStart(2, '0')}:
                    {String(promoCountdown.minutes).padStart(2, '0')}:
                    {String(promoCountdown.seconds).padStart(2, '0')}
                  </span>
                </div>
              )}
              {promoCountdown?.expired && (
                <span className="text-xs text-red-400 ml-2 px-2 py-1 bg-red-500/20 rounded">
                  ⏰ Sale Ended
                </span>
              )}
            </div>
            {promotion.discount_code && (
              <Button
                variant="outline"
                size="sm"
                onClick={copyDiscountCode}
                className={`${
                  promotion.banner_color === 'green' ? 'border-green-500/50 text-green-300 hover:bg-green-500/20' :
                  promotion.banner_color === 'blue' ? 'border-blue-500/50 text-blue-300 hover:bg-blue-500/20' :
                  promotion.banner_color === 'red' ? 'border-red-500/50 text-red-300 hover:bg-red-500/20' :
                  promotion.banner_color === 'purple' ? 'border-purple-500/50 text-purple-300 hover:bg-purple-500/20' :
                  'border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/20'
                }`}
              >
                <Copy className="w-3 h-3 mr-1" />
                Code: <span className="font-mono font-bold ml-1">{promotion.discount_code}</span>
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ========== VISITOR LANDING PAGE (Not Logged In) ========== */}
      {!guestUser && (
        <div className="max-w-4xl mx-auto py-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Activity className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('smartTradingSignals')}
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              {t('makeInformedDecisions')}
            </p>
            
            {/* Sign In Button */}
            <Button
              onClick={() => setShowAuthModal(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg px-8 py-6 rounded-xl shadow-lg shadow-purple-500/25"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              {t('getStartedSignIn')}
            </Button>
          </div>

          {/* Inspirational Quote */}
          <div className="text-center mb-12 px-4">
            <div className="relative inline-block">
              <span className="text-6xl absolute -top-4 -left-6 text-blue-500/30">&ldquo;</span>
              <p className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent px-8">
                {t('missQuote')}
              </p>
              <span className="text-6xl absolute -bottom-8 -right-4 text-pink-500/30">&rdquo;</span>
            </div>
            <p className="text-gray-400 mt-6 text-lg">
              — <span className="text-white font-medium">{t('wayneGretzky')}</span>
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 text-center transform hover:scale-105 transition-all">
              <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{t('aiPoweredSignals')}</h3>
              <p className="text-gray-400 text-sm">{t('aiPoweredSignalsDesc')}</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 text-center transform hover:scale-105 transition-all">
              <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{t('trackYourSuccess')}</h3>
              <p className="text-gray-400 text-sm">{t('trackYourSuccessDesc')}</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 text-center transform hover:scale-105 transition-all">
              <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{t('topPerformersTitle')}</h3>
              <p className="text-gray-400 text-sm">{t('topPerformersDesc')}</p>
            </div>
          </div>

          {/* Stats Preview */}
          <div className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 border border-white/10 rounded-2xl p-8 mb-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-3xl md:text-4xl font-bold text-green-400">$20</p>
                <p className="text-gray-400 text-sm">{t('startingFrom')}</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-blue-400">50+</p>
                <p className="text-gray-400 text-sm">{t('tradingPairs')}</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-purple-400">24/7</p>
                <p className="text-gray-400 text-sm">{t('systemAvailability')}</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-pink-400">{t('free')}</p>
                <p className="text-gray-400 text-sm">{t('freeTrialAccess')}</p>
              </div>
            </div>
          </div>

          {/* Services Grid - TradingView & Coding Service */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {/* TradingView Smart Indicator Showcase */}
            <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-white/10 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                    <BarChart2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{t('tradingViewIndicator')}</h3>
                    <p className="text-xs text-gray-400">{t('tradingViewIndicatorDesc')}</p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bonbot-profiles/artifacts/d9qktl6x_Screenshot%202026-01-10%20214604.png"
                  alt="TradingView Smart Indicator"
                  className="w-full h-[250px] object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 to-transparent p-3">
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full border border-green-500/30">
                      {t('forexMarkets')}
                    </span>
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full border border-blue-500/30">
                      {t('futuresMarkets')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Coding Service */}
            <div 
              className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-white/10 rounded-2xl overflow-hidden cursor-pointer hover:border-orange-500/50 transition-all group"
              onClick={() => setSupportExpanded(true)}
            >
              <div className="p-4 border-b border-white/10 bg-gradient-to-r from-blue-600/20 to-orange-600/20">
                <h3 className="text-lg font-bold text-white text-center">{t('codingServiceTitle')}</h3>
                <p className="text-sm text-orange-400 text-center font-medium">{t('codingServiceCta')}</p>
              </div>
              <div className="relative flex items-center justify-center py-6 px-8 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
                <div className="relative w-48 h-48 rounded-2xl overflow-hidden shadow-2xl border-2 border-orange-500/30">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_bonbot-profiles/artifacts/vhzscxby_coding.jpeg"
                    alt="Coding Service"
                    className="w-full h-full object-cover scale-150"
                    style={{ objectPosition: 'center 40%' }}
                  />
                </div>
              </div>
              <div className="p-4 text-center">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSupportExpanded(true);
                    setTimeout(() => {
                      document.querySelector('[data-testid="support-section"]')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-orange-600 hover:from-blue-700 hover:to-orange-700 text-white font-bold rounded-lg transition-all group-hover:scale-105"
                >
                  {t('contactUsNow')}
                </button>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <p className="text-gray-400 mb-4">{t('joinTraders')}</p>
            <Button
              onClick={() => setShowAuthModal(true)}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 px-8 py-4"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {t('createFreeAccount')}
            </Button>
          </div>

          {/* Public Reviews Section - Visible to visitors */}
          <PublicReviewsSection />

          {/* Support Ticket Section - Collapsible */}
          <div data-testid="support-section" className="mt-12 bg-gradient-to-r from-slate-800/50 to-slate-900/50 border border-white/10 rounded-2xl overflow-hidden">
            <button
              onClick={() => setSupportExpanded(!supportExpanded)}
              className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-xl font-bold text-white">{t('needHelp')}</h2>
                  <p className="text-sm text-gray-400">{t('supportDescription')}</p>
                </div>
              </div>
              <ChevronDown className={`w-6 h-6 text-gray-400 transition-transform ${supportExpanded ? 'rotate-180' : ''}`} />
            </button>
            
            {supportExpanded && (
              <div className="px-6 pb-6">
                {supportSuccess ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">{t('ticketSubmitted')}</h3>
                    <p className="text-gray-400 mb-4">{t('ticketSubmittedDesc')}</p>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 max-w-md mx-auto">
                      <p className="text-yellow-400 text-sm flex items-center justify-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {t('checkSpamFolder')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSupportTicket} className="max-w-lg mx-auto space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">{t('yourName')} *</label>
                        <Input
                          value={supportName}
                          onChange={(e) => setSupportName(e.target.value)}
                          placeholder={t('enterYourName')}
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">{t('yourEmail')} *</label>
                        <Input
                          type="email"
                          value={supportEmail}
                          onChange={(e) => setSupportEmail(e.target.value)}
                          placeholder={t('enterYourEmail')}
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">{t('subject')} *</label>
                      <Input
                        value={supportSubject}
                        onChange={(e) => setSupportSubject(e.target.value)}
                        placeholder={t('enterSubject')}
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">{t('message')} *</label>
                      <textarea
                        value={supportMessage}
                        onChange={(e) => setSupportMessage(e.target.value)}
                        placeholder={t('enterMessage')}
                        rows={4}
                        className="w-full bg-white/10 border border-white/20 text-white placeholder:text-gray-500 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={supportLoading}
                      className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
                    >
                      {supportLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      {supportLoading ? t('submitting') : t('submitTicket')}
                    </Button>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* Disclaimer Section */}
          <div className="mt-12 bg-yellow-500/5 border border-yellow-500/20 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{t('disclaimer')}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {t('disclaimerText')}
                </p>
              </div>
            </div>
          </div>

          {/* Policy Sections - Visible to visitors */}
          <PolicySections />
        </div>
      )}

      {/* ========== LOGGED IN USER CONTENT ========== */}
      {guestUser && (
        <>
      {/* Top Performers Section */}
      <div className="max-w-4xl mx-auto mb-6">
        {/* Section Header with Guide Me Now Button */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            <h2 className="text-lg font-bold text-white">Top Performers</h2>
          </div>
          <button
            onClick={openCoachingModal}
            data-testid="guide-me-now-btn"
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-sm font-bold rounded-xl transition-all transform hover:scale-105 shadow-lg shadow-purple-500/25"
          >
            <User className="w-4 h-4" />
            Guide me now
          </button>
        </div>
        
        {/* PRO Upgrade Banner for non-PRO users */}
        {!guestUser?.has_pro_access && (
          <div 
            onClick={() => setShowProPromo(true)}
            className="mb-4 bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-red-500/20 border border-yellow-500/30 rounded-xl p-3 cursor-pointer hover:from-yellow-500/30 hover:via-orange-500/30 hover:to-red-500/30 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg animate-pulse">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Upgrade to PRO Scalper Bot</p>
                  <p className="text-xs text-yellow-300/80">Advanced signals • Higher accuracy • Financial planner</p>
                </div>
              </div>
              <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-xs font-bold rounded-lg hover:from-yellow-600 hover:to-orange-700 transition-all group-hover:scale-105 shadow-lg">
                <Zap className="w-3 h-3" />
                Watch Demo
              </button>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Trading Session Manager */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">Session Manager</h3>
                  <p className="text-xs text-gray-500">Smart money management</p>
                </div>
              </div>
              {sessionActive && (
                <Badge className={`${sessionStats.currentPnL >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  ${sessionStats.currentPnL.toFixed(2)}
                </Badge>
              )}
            </div>
            
            {/* Cooling Period Warning with Reason */}
            {coolingUntil && (
              <div className={`border rounded-lg p-4 mb-3 ${
                coolingReason === 'target' ? 'bg-green-500/10 border-green-500/30' :
                coolingReason === 'stop' ? 'bg-red-500/10 border-red-500/30' :
                coolingReason === 'three_consecutive_losses' ? 'bg-orange-500/10 border-orange-500/30' :
                coolingReason === 'profit_protection' ? 'bg-blue-500/10 border-blue-500/30' :
                'bg-amber-500/10 border-amber-500/30'
              }`}>
                {/* Reason Display with Emoji */}
                <div className="text-center mb-3">
                  <p className="text-2xl mb-1">
                    {coolingReason === 'target' ? '🎯' :
                     coolingReason === 'stop' ? '🛑' :
                     coolingReason === 'three_consecutive_losses' ? '❌❌❌' :
                     coolingReason === 'profit_protection' ? '🛡️' :
                     '⏸️'}
                  </p>
                  <p className={`text-lg font-bold ${
                    coolingReason === 'target' ? 'text-green-400' :
                    coolingReason === 'stop' ? 'text-red-400' :
                    coolingReason === 'three_consecutive_losses' ? 'text-orange-400' :
                    coolingReason === 'profit_protection' ? 'text-blue-400' :
                    'text-amber-400'
                  }`}>
                    {coolingReason === 'target' ? 'TARGET REACHED!' :
                     coolingReason === 'stop' ? 'STOP LOSS HIT' :
                     coolingReason === 'three_consecutive_losses' ? '3 CONSECUTIVE LOSSES' :
                     coolingReason === 'profit_protection' ? 'PROFIT PROTECTED' :
                     'SESSION ENDED'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {coolingReason === 'target' ? 'Great job! You hit your profit target.' :
                     coolingReason === 'stop' ? 'Risk limit reached. Take a break and review.' :
                     coolingReason === 'three_consecutive_losses' ? 'Protecting your profit from further losses.' :
                     coolingReason === 'profit_protection' ? 'Your profits have been locked in.' :
                     'Time for a break.'}
                  </p>
                </div>
                
                {/* Cooling Timer */}
                <div className="flex items-center justify-center gap-2 text-amber-400 bg-black/20 rounded-lg py-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">Cooling Period Active</span>
                </div>
                <p className="text-xs text-gray-400 text-center mt-2">
                  Ends at: {coolingUntil.toLocaleTimeString()} ({Math.ceil((coolingUntil - new Date()) / 60000)} min remaining)
                </p>
              </div>
            )}
            
            {/* Session Setup Form */}
            {showSessionSetup && !coolingUntil ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Account Size ($)</label>
                    <input
                      type="number"
                      value={sessionConfig.accountSize || ''}
                      onChange={(e) => setSessionConfig(prev => ({...prev, accountSize: parseFloat(e.target.value) || 0}))}
                      onFocus={(e) => e.target.select()}
                      placeholder="100"
                      className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Risk/Stop ($)</label>
                    <input
                      type="number"
                      value={sessionConfig.riskAmount || ''}
                      onChange={(e) => setSessionConfig(prev => ({...prev, riskAmount: parseFloat(e.target.value) || 0}))}
                      onFocus={(e) => e.target.select()}
                      placeholder="10"
                      className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Profit Target ($)</label>
                    <input
                      type="number"
                      value={sessionConfig.profitTarget || ''}
                      onChange={(e) => setSessionConfig(prev => ({...prev, profitTarget: parseFloat(e.target.value) || 0}))}
                      onFocus={(e) => e.target.select()}
                      placeholder="20"
                      className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Session (min)</label>
                    <input
                      type="number"
                      value={sessionConfig.sessionMinutes || ''}
                      onChange={(e) => setSessionConfig(prev => ({...prev, sessionMinutes: parseInt(e.target.value) || 0}))}
                      onFocus={(e) => e.target.select()}
                      placeholder="30"
                      className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Payout %</label>
                    <select
                      value={sessionConfig.payoutPercent}
                      onChange={(e) => setSessionConfig(prev => ({...prev, payoutPercent: parseInt(e.target.value)}))}
                      className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                    >
                      <option value={80}>80%</option>
                      <option value={85}>85%</option>
                      <option value={90}>90%</option>
                      <option value={92}>92%</option>
                      <option value={95}>95%</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Total Trades</label>
                    <input
                      type="number"
                      value={sessionConfig.totalTrades || ''}
                      onChange={(e) => setSessionConfig(prev => ({...prev, totalTrades: parseInt(e.target.value) || 1}))}
                      onFocus={(e) => e.target.select()}
                      placeholder="5"
                      className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                      min="1"
                      max="20"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Session #</label>
                    <select
                      value={sessionConfig.sessionNumber}
                      onChange={(e) => setSessionConfig(prev => ({...prev, sessionNumber: parseInt(e.target.value)}))}
                      className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                    >
                      {[...Array(15)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>Session {i + 1}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Cool Down (min)</label>
                    <input
                      type="number"
                      value={sessionConfig.coolingPeriod || ''}
                      onChange={(e) => setSessionConfig(prev => ({...prev, coolingPeriod: parseInt(e.target.value) || 5}))}
                      onFocus={(e) => e.target.select()}
                      placeholder="15"
                      className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                      min="5"
                      max="60"
                    />
                  </div>
                </div>
                
                {/* Daily Stats Summary */}
                <div className="bg-gradient-to-r from-slate-800/80 to-slate-700/50 border border-white/10 rounded-lg p-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-400 font-medium">Today&apos;s Stats</span>
                    <button
                      onClick={resetDailyStats}
                      className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                    >
                      <RefreshCw className="w-3 h-3" /> Reset
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div>
                      <p className="text-gray-500">P&L</p>
                      <p className={`font-bold ${dailyStats.totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ${dailyStats.totalPnL.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">W/L</p>
                      <p className="font-bold">
                        <span className="text-green-400">{dailyStats.totalWins}</span>
                        <span className="text-gray-500">/</span>
                        <span className="text-red-400">{dailyStats.totalLosses}</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Sessions</p>
                      <p className="font-bold text-white">{dailyStats.sessionsCompleted}</p>
                    </div>
                  </div>
                </div>
                
                {/* Session Preview */}
                {sessionConfig.accountSize > 0 && sessionConfig.profitTarget > 0 && (
                  <div className="space-y-2">
                    {/* Win Scenario Selector */}
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-400 font-medium">Target Wins to Hit ${sessionConfig.profitTarget}</span>
                        <select
                          value={targetWins || 'auto'}
                          onChange={(e) => setTargetWins(e.target.value === 'auto' ? null : parseInt(e.target.value))}
                          className="bg-slate-700 border border-white/10 rounded px-2 py-1 text-xs text-white"
                        >
                          <option value="auto">Auto (Recommended)</option>
                          {calculateWinScenarios(sessionConfig).filter(s => s.isViable).map(scenario => (
                            <option key={scenario.wins} value={scenario.wins}>
                              {scenario.wins}W / {scenario.losses}L ({scenario.winRate}% WR) - ${scenario.requiredLot}/trade
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Scenario Details */}
                      {(() => {
                        const plan = calculateSessionPlan(sessionConfig, targetWins);
                        const scenarios = calculateWinScenarios(sessionConfig);
                        const selectedScenario = targetWins 
                          ? scenarios.find(s => s.wins === targetWins)
                          : scenarios.find(s => s.isRecommended) || scenarios[Math.floor(scenarios.length / 2)];
                        
                        return (
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between text-gray-400">
                              <span>Lot Size:</span>
                              <span className="text-amber-400 font-bold">${plan.baseLotSize}</span>
                            </div>
                            <div className="flex justify-between text-gray-400">
                              <span>Need to Win:</span>
                              <span className="text-green-400 font-bold">{plan.requiredWins} of {sessionConfig.totalTrades} trades</span>
                            </div>
                            <div className="flex justify-between text-gray-400">
                              <span>Can Lose:</span>
                              <span className="text-red-400 font-bold">{sessionConfig.totalTrades - plan.requiredWins} trades</span>
                            </div>
                            <div className="flex justify-between text-gray-400">
                              <span>Profit/Win:</span>
                              <span className="text-white">${plan.profitPerWin}</span>
                            </div>
                            <div className="flex justify-between text-gray-400">
                              <span>Max Risk:</span>
                              <span className="text-white">${plan.maxRisk || (plan.baseLotSize * (sessionConfig.totalTrades - plan.requiredWins)).toFixed(2)}</span>
                            </div>
                            
                            {/* Visual Win/Loss Bar */}
                            <div className="mt-2 pt-2 border-t border-white/10">
                              <div className="flex gap-0.5">
                                {[...Array(sessionConfig.totalTrades)].map((_, i) => (
                                  <div 
                                    key={i}
                                    className={`h-3 flex-1 rounded-sm ${
                                      i < plan.requiredWins 
                                        ? 'bg-green-500' 
                                        : 'bg-red-500/50'
                                    }`}
                                    title={i < plan.requiredWins ? 'Win needed' : 'Can lose'}
                                  />
                                ))}
                              </div>
                              <p className="text-[10px] text-gray-500 mt-1 text-center">
                                Win {plan.requiredWins} green trades to hit target
                              </p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Quick Scenario Buttons */}
                    <div className="flex gap-1">
                      {calculateWinScenarios(sessionConfig)
                        .filter(s => s.isViable)
                        .slice(0, 4)
                        .map(scenario => (
                          <button
                            key={scenario.wins}
                            onClick={() => setTargetWins(scenario.wins)}
                            className={`flex-1 py-1 px-1 rounded text-[10px] font-medium transition-all ${
                              targetWins === scenario.wins
                                ? 'bg-amber-500 text-black'
                                : scenario.isRecommended
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                            }`}
                          >
                            {scenario.wins}W/{scenario.losses}L
                          </button>
                        ))}
                    </div>
                  </div>
                )}
                
                <button
                  onClick={() => setShowCompoundPlan(v => !v)}
                  className="w-full py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 border border-purple-500/30 text-purple-300 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2"
                  data-testid="compound-plan-toggle"
                >
                  <TrendingUp className="w-3.5 h-3.5" />
                  {showCompoundPlan ? 'Hide Compound Goal Plan' : '🔁 Compound Interest Plan'}
                </button>

                <CompoundPlanCard
                  open={showCompoundPlan}
                  onClose={() => setShowCompoundPlan(false)}
                  defaultBalance={sessionConfig.accountSize || 50}
                  defaultEntry={sessionConfig.riskAmount || 5}
                  defaultPayout={sessionConfig.payoutPercent || 92}
                  traderName={guestUser?.name || guestUser?.email?.split('@')[0] || ''}
                />

                <button
                  onClick={startTradingSession}
                  disabled={!sessionConfig.accountSize || !sessionConfig.profitTarget || !sessionConfig.riskAmount}
                  className="w-full py-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white rounded-lg text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Start Session {sessionConfig.sessionNumber}
                </button>
              </div>
            ) : sessionActive ? (
              /* Active Session View */
              <div className="space-y-3">
                {/* Phase Indicator */}
                <div className={`text-center py-1 rounded-lg text-xs font-bold ${
                  sessionStats.phase === 'RECOVERY' ? 'bg-red-500/20 text-red-400' :
                  sessionStats.phase === 'BUILD' ? 'bg-blue-500/20 text-blue-400' :
                  sessionStats.phase === 'ACCELERATE' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-green-500/20 text-green-400'
                }`}>
                  {sessionStats.phase === 'RECOVERY' ? '🔄 RECOVERY MODE' :
                   sessionStats.phase === 'BUILD' ? '🏗️ BUILDING CUSHION' :
                   sessionStats.phase === 'ACCELERATE' ? '🚀 ACCELERATING' :
                   '🎯 CLOSING OUT'}
                  {sessionStats.winStreak >= 2 && <span className="ml-2">🔥 {sessionStats.winStreak} streak</span>}
                  {sessionStats.lossStreak >= 2 && <span className="ml-2">❄️ {sessionStats.lossStreak} cold</span>}
                </div>
                
                {/* Session Stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-800/50 rounded-lg p-2">
                    <p className="text-xs text-gray-400">P&L</p>
                    <p className={`text-lg font-bold ${sessionStats.currentPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${sessionStats.currentPnL.toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2">
                    <p className="text-xs text-gray-400">W/L</p>
                    <p className="text-lg font-bold">
                      <span className="text-green-400">{sessionStats.wins}</span>
                      <span className="text-gray-500">/</span>
                      <span className="text-red-400">{sessionStats.losses}</span>
                    </p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2">
                    <p className="text-xs text-gray-400">To Target</p>
                    <p className={`text-lg font-bold ${(sessionConfig.profitTarget - sessionStats.currentPnL) <= 0 ? 'text-green-400' : 'text-amber-400'}`}>
                      ${Math.max(0, sessionConfig.profitTarget - sessionStats.currentPnL).toFixed(2)}
                    </p>
                  </div>
                </div>
                
                {/* Wins Still Needed */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 text-center">
                  <p className="text-xs text-gray-400">Wins needed to hit ${sessionConfig.profitTarget} target</p>
                  <p className="text-xl font-bold text-amber-400">
                    {sessionStats.requiredWins > 0 ? `${sessionStats.requiredWins} more wins` : '🎯 TARGET READY!'}
                  </p>
                  {sessionStats.tradesRemaining <= 0 && sessionStats.requiredWins > 0 && (
                    <p className="text-[10px] text-amber-300 mt-1">Planned trades done - keep going to hit target!</p>
                  )}
                </div>
                
                {/* Progress to Target */}
                <div className="bg-slate-800/50 rounded-lg p-2">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Progress to Target</span>
                    <span>{Math.min(100, Math.max(0, (sessionStats.currentPnL / sessionConfig.profitTarget * 100))).toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${sessionStats.currentPnL >= 0 ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-red-500 to-red-600'}`}
                      style={{ width: `${Math.min(100, Math.max(0, Math.abs(sessionStats.currentPnL) / sessionConfig.profitTarget * 100))}%` }}
                    />
                  </div>
                </div>
                
                {/* Profit Protection Info - Show when we have profit to protect */}
                {sessionStats.peakProfit > 0 && sessionStats.protectedFloor > 0 && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-3 h-3 text-blue-400" />
                      <span className="text-xs text-blue-400 font-medium">Profit Protection Active</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-gray-500">Peak Profit</p>
                        <p className="text-green-400 font-bold">${sessionStats.peakProfit.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Protected Floor</p>
                        <p className="text-blue-400 font-bold">${sessionStats.protectedFloor.toFixed(2)}</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Session stops if P&L drops to ${sessionStats.protectedFloor.toFixed(2)}
                    </p>
                  </div>
                )}
                
                {/* Next Trade Suggestion */}
                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-400">Next Trade Lot Size</span>
                    <span className="text-xs text-amber-400">Wins needed: {sessionStats.requiredWins}</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-400 text-center">
                    ${sessionStats.currentLotSize.toFixed(2)}
                  </p>
                </div>
                
                {/* Report Trade Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => reportSessionTrade('win')}
                    className="py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-lg font-bold flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" /> WIN
                  </button>
                  <button
                    onClick={() => reportSessionTrade('loss')}
                    className="py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white rounded-lg font-bold flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-4 h-4" /> LOSS
                  </button>
                </div>
                
                {/* Trade History */}
                {sessionTrades.length > 0 && (
                  <div className="max-h-32 overflow-y-auto">
                    <p className="text-xs text-gray-400 mb-1">Trade History</p>
                    {sessionTrades.slice().reverse().map((trade, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-white/5">
                        <span className="text-gray-400">#{trade.tradeNumber}</span>
                        <span className={trade.result === 'win' ? 'text-green-400' : 'text-red-400'}>
                          {trade.result.toUpperCase()}
                        </span>
                        <span className="text-gray-400">${trade.lotSize}</span>
                        <span className={trade.pnlChange >= 0 ? 'text-green-400' : 'text-red-400'}>
                          {trade.pnlChange >= 0 ? '+' : ''}{trade.pnlChange.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* End Session Button */}
                <button
                  onClick={resetSession}
                  className="w-full py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-xs"
                >
                  End Session Early
                </button>
                
                {/* Daily Stats in Active Session */}
                <div className="bg-slate-800/30 rounded-lg p-2 mt-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Day Total:</span>
                    <span className={`font-bold ${(dailyStats.totalPnL + sessionStats.currentPnL) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${(dailyStats.totalPnL + sessionStats.currentPnL).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              /* Session Ended - Show Summary */
              <div className="text-center py-4">
                <Trophy className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                <p className="text-sm text-gray-400">Session Complete</p>
                <p className={`text-xl font-bold ${sessionStats.currentPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  ${sessionStats.currentPnL.toFixed(2)}
                </p>
                
                {/* Daily Summary */}
                <div className="bg-slate-800/50 rounded-lg p-3 mt-3 text-left">
                  <p className="text-xs text-gray-400 mb-2 text-center">Today&apos;s Total</p>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div>
                      <p className="text-gray-500">P&L</p>
                      <p className={`font-bold ${dailyStats.totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ${dailyStats.totalPnL.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">W/L</p>
                      <p className="font-bold">
                        <span className="text-green-400">{dailyStats.totalWins}</span>
                        <span className="text-gray-500">/</span>
                        <span className="text-red-400">{dailyStats.totalLosses}</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Sessions</p>
                      <p className="font-bold text-white">{dailyStats.sessionsCompleted}</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setShowSessionSetup(true)}
                    className="flex-1 px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg text-sm hover:bg-amber-500/30"
                  >
                    New Session
                  </button>
                  <button
                    onClick={resetDailyStats}
                    className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm hover:bg-red-500/20"
                    title="Reset daily stats to 0"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Flipable: Stable Assets / Compound Interest Calculator */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
            {/* Header with flip toggle */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                  showCompoundCalc 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                    : 'bg-gradient-to-r from-blue-500 to-cyan-600'
                }`}>
                  {showCompoundCalc ? (
                    <Calculator className="w-4 h-4 text-white" />
                  ) : (
                    <TrendingUp className="w-4 h-4 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">
                    {showCompoundCalc ? 'Compound Interest Plan' : t('mostStableAssets')}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {showCompoundCalc ? '30-Day Growth Calculator' : t('stableAssetsDesc')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCompoundCalc(!showCompoundCalc)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all group"
                title={showCompoundCalc ? 'Show Stable Assets' : 'Show Compound Calculator'}
              >
                <RotateCcw className="w-4 h-4 text-gray-400 group-hover:text-white transition-transform group-hover:rotate-180" />
              </button>
            </div>
            
            {/* Content based on flip state */}
            {showCompoundCalc ? (
              /* Compound Interest Calculator */
              <div className="space-y-3">
                {/* Input Form */}
                {!compoundPlan && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] text-gray-400 mb-1 block">Starting $</label>
                        <input
                          type="number"
                          value={compoundConfig.startingBalance}
                          onChange={(e) => setCompoundConfig({...compoundConfig, startingBalance: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-800 border border-white/20 rounded px-2 py-1.5 text-white text-sm focus:border-green-500 focus:outline-none"
                          placeholder="100"
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-400 mb-1 block">Daily %</label>
                        <input
                          type="number"
                          value={compoundConfig.dailyInterest}
                          onChange={(e) => setCompoundConfig({...compoundConfig, dailyInterest: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-800 border border-white/20 rounded px-2 py-1.5 text-white text-sm focus:border-green-500 focus:outline-none"
                          placeholder="10"
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-400 mb-1 block">Days</label>
                        <select
                          value={compoundConfig.durationDays}
                          onChange={(e) => setCompoundConfig({...compoundConfig, durationDays: parseInt(e.target.value)})}
                          className="w-full bg-slate-800 border border-white/20 rounded px-2 py-1.5 text-white text-sm focus:border-green-500 focus:outline-none"
                        >
                          {[7, 14, 21, 30].map(d => (
                            <option key={d} value={d}>{d} days</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <Button
                      onClick={calculateCompoundPlan}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-sm py-2"
                    >
                      <Calculator className="w-4 h-4 mr-2" />
                      Generate Plan
                    </Button>
                  </div>
                )}
                
                {/* Plan Results */}
                {compoundPlan && (
                  <div className="space-y-2">
                    {/* Summary */}
                    <div className="flex items-center justify-between p-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-400">Total Growth</p>
                        <p className="text-lg font-bold text-green-400">+${compoundPlan.totalGrowth}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">{compoundPlan.days.length} Days</p>
                        <p className="text-lg font-bold text-emerald-400">+{compoundPlan.growthPercent}%</p>
                      </div>
                    </div>
                    
                    {/* Daily Plan - Scrollable */}
                    <div className="max-h-[280px] overflow-y-auto space-y-1 pr-1">
                      {compoundPlan.days.map((day) => (
                        <div 
                          key={day.day}
                          className={`flex items-center justify-between p-2 rounded-lg border transition-all ${
                            compoundChecks[day.day] === 'achieved' 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : compoundChecks[day.day] === 'failed'
                              ? 'bg-red-500/10 border-red-500/30'
                              : 'bg-white/5 border-white/10'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-white">
                              {day.day}
                            </span>
                            <div>
                              <p className="text-xs text-gray-400">Start: ${day.startBalance}</p>
                              <p className="text-sm font-medium text-green-400">Target: +${day.dailyTarget}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 mr-2">${day.endBalance}</span>
                            {/* Check/X buttons */}
                            <button
                              onClick={() => toggleDayCheck(day.day, 'achieved')}
                              className={`w-6 h-6 rounded flex items-center justify-center transition-all ${
                                compoundChecks[day.day] === 'achieved'
                                  ? 'bg-green-500 text-white'
                                  : 'bg-slate-700 text-gray-400 hover:bg-green-500/50'
                              }`}
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => toggleDayCheck(day.day, 'failed')}
                              className={`w-6 h-6 rounded flex items-center justify-center transition-all ${
                                compoundChecks[day.day] === 'failed'
                                  ? 'bg-red-500 text-white'
                                  : 'bg-slate-700 text-gray-400 hover:bg-red-500/50'
                              }`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Progress Summary */}
                    <div className="flex items-center justify-between p-2 bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Check className="w-4 h-4 text-green-400" />
                          <span className="text-xs text-green-400">
                            {Object.values(compoundChecks).filter(v => v === 'achieved').length}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <X className="w-4 h-4 text-red-400" />
                          <span className="text-xs text-red-400">
                            {Object.values(compoundChecks).filter(v => v === 'failed').length}
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={resetCompoundPlan}
                        variant="ghost"
                        size="sm"
                        className="text-xs text-gray-400 hover:text-red-400"
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Reset
                      </Button>
                    </div>

                    {/* Download PDF */}
                    <button
                      onClick={downloadCompoundPlanPdf}
                      disabled={downloadingCompoundPdf}
                      data-testid="compound-daily-download-pdf"
                      className="w-full py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 disabled:opacity-60 disabled:cursor-wait text-white font-bold text-xs flex items-center justify-center gap-2 transition-all"
                    >
                      <Download className="w-3.5 h-3.5" />
                      {downloadingCompoundPdf ? 'Generating PDF…' : 'Download Plan as PDF'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Stable Assets List */
              <>
                {loadingTopPerformers ? (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : topPerformers?.top_stable?.length > 0 ? (
                  <div className="space-y-2">
                    {topPerformers.top_stable.map((asset, idx) => (
                      <div 
                        key={asset.symbol} 
                        className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all cursor-pointer"
                        onClick={() => setSelectedManualPair(asset.symbol)}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-blue-500 text-white' :
                            idx === 1 ? 'bg-blue-400 text-white' :
                            'bg-blue-300/50 text-blue-100'
                          }`}>
                            {idx + 1}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-white">{asset.name}</p>
                            <div className="flex items-center gap-1">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                asset.trend === 'STABLE' ? 'bg-green-500/20 text-green-300' :
                                asset.trend === 'RANGING' ? 'bg-blue-500/20 text-blue-300' :
                                'bg-red-500/20 text-red-300'
                              }`}>
                                {asset.trend}
                              </span>
                              <span className="text-[10px] text-gray-500">
                                {asset.clear_signals} clear signals
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-gray-400">{t('stability')}</span>
                          <div className="flex items-center gap-1">
                            <div className="w-12 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${
                                  asset.stability_score > 30 ? 'bg-green-500' :
                                  asset.stability_score > 20 ? 'bg-blue-500' : 'bg-yellow-500'
                                }`}
                                style={{ width: `${Math.min(asset.stability_score * 2, 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-400">{asset.stability_score}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <TrendingUp className="w-6 h-6 mx-auto mb-1 opacity-50" />
                    <p>{t('analyzingAssets')}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-8">
        {/* Subscription Expired Banner */}
        {guestUser?.subscription_expired && (
          <div className="mb-6 p-4 bg-gradient-to-r from-red-900/50 to-orange-900/50 border border-red-500/30 rounded-xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Subscription Expired</h3>
                  <p className="text-red-300 text-sm">Your {guestUser.current_plan || 'plan'} has expired. Renew now to continue using the signal generator.</p>
                </div>
              </div>
              <button
                onClick={() => setShowRenewalModal(true)}
                className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold rounded-lg transition-all whitespace-nowrap"
              >
                Renew Now →
              </button>
            </div>
            <p className="text-gray-400 text-xs mt-3">Note: After renewal, it may take up to 30 minutes for your access to be restored.</p>
          </div>
        )}
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{t('signalGenerator')}</h1>
              <p className="text-gray-400">{t('tradingPortal')}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Language Selector */}
            <LanguageSelector />
            
            {/* Admin Link */}
            <RouterLink to="/admin">
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-white/10"
              >
                <Shield className="w-4 h-4 mr-1" />
                {t('admin')}
              </Button>
            </RouterLink>
            
            {guestUser ? (
              <div className="flex items-center gap-2">
                {/* Referral Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    setShowReferralModal(true);
                    setReferralInfo(null); // Reset to show loading
                    // Fetch fresh referral info when modal opens
                    const guestToken = localStorage.getItem('guestToken');
                    console.log('Fetching referral info with token:', guestToken?.substring(0, 20) + '...');
                    if (guestToken) {
                      try {
                        const response = await fetch(`${API}/user/referral-info?token=${guestToken}`);
                        const data = await response.json();
                        console.log('Referral API response:', data);
                        setReferralInfo(data);
                      } catch (err) {
                        console.error('Referral fetch error:', err);
                        setReferralInfo({ success: false, message: err.message });
                      }
                    } else {
                      console.log('No guest token found in localStorage');
                      setReferralInfo({ success: false, message: 'No token found' });
                    }
                  }}
                  className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                >
                  <Share2 className="w-4 h-4" />
                </Button>
                
                {/* Education Notification Bell */}
                <EducationNotificationBell 
                  count={educationNotifications} 
                  onClick={() => setShowEducationTV(true)} 
                />
                
                {/* Messenger Button */}
                <MessengerButton 
                  onClick={() => setShowMessenger(true)} 
                  onNewMessage={() => setShowMessenger(true)}
                />
                
                {/* User Profile Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 text-green-300 border border-green-500/30 hover:bg-green-500/30 transition-colors"
                  >
                    <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {guestUser.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <span className="text-sm font-medium">{guestUser.name}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showProfileDropdown && (
                    <>
                      {/* Backdrop to close dropdown */}
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowProfileDropdown(false)}
                      />
                      <div className="absolute right-0 mt-2 w-56 bg-slate-800 border border-white/20 rounded-xl shadow-xl z-50 overflow-hidden">
                        {/* User Info Header */}
                        <div className="p-3 border-b border-white/10 bg-white/5">
                          <p className="text-white font-medium">{guestUser.name}</p>
                          <p className="text-xs text-gray-400">{guestUser.email}</p>
                          
                          {/* Subscription Status */}
                          <div className="mt-2 pt-2 border-t border-white/10">
                            {guestUser.subscription_expired ? (
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-red-400 font-medium">⚠️ Plan Expired</span>
                                <button 
                                  onClick={() => {
                                    setShowProfileDropdown(false);
                                    setShowRenewalModal(true);
                                  }}
                                  className="text-xs bg-gradient-to-r from-green-500 to-emerald-600 text-white px-2 py-1 rounded font-medium hover:from-green-600 hover:to-emerald-700"
                                >
                                  Renew Now
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-400">Plan:</span>
                                <span className="text-xs text-green-400 font-medium">{guestUser.current_plan || 'Active'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Menu Items */}
                        <div className="py-1">
                          <button
                            onClick={() => {
                              setShowProfileDropdown(false);
                              setShowProfileModal(true);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-white/10 flex items-center gap-3"
                          >
                            <User className="w-4 h-4 text-blue-400" />
                            View Profile
                          </button>
                          <button
                            onClick={() => {
                              setShowProfileDropdown(false);
                              setShowProfileModal(true);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-white/10 flex items-center gap-3"
                          >
                            <Settings className="w-4 h-4 text-gray-400" />
                            Edit Profile
                          </button>
                        </div>
                        
                        {/* Pro Upgrade Section */}
                        <div className="border-t border-white/10 py-1">
                          {guestUser?.has_pro_access ? (
                            <button
                              onClick={() => {
                                setShowProfileDropdown(false);
                                setShowProMode(true);
                                localStorage.setItem('showProMode', 'true');
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm hover:bg-purple-500/10 flex items-center gap-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10"
                            >
                              <Crown className="w-4 h-4 text-yellow-400" />
                              <span className="text-purple-300 font-medium">Go Pro</span>
                              <span className="ml-auto text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 rounded-full">PRO</span>
                            </button>
                          ) : (
                            <div className="space-y-1">
                              <button
                                onClick={() => {
                                  setShowProfileDropdown(false);
                                  setShowUpgradeModal(true);
                                }}
                                className="w-full px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-purple-500/10 flex items-center gap-3"
                              >
                                <Zap className="w-4 h-4 text-purple-400" />
                                <span>Upgrade to Pro</span>
                                {guestUser?.pro_upgrade_pending && (
                                  <span className="ml-auto text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">Pending</span>
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setShowProfileDropdown(false);
                                  setShowProPromo(true);
                                }}
                                className="w-full px-4 py-2 text-left text-xs text-yellow-400/70 hover:text-yellow-400 hover:bg-yellow-500/10 flex items-center gap-2 pl-8"
                              >
                                <Crown className="w-3 h-3" />
                                Watch PRO Demo
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Logout */}
                        <div className="border-t border-white/10 py-1">
                          <button
                            onClick={() => {
                              setShowProfileDropdown(false);
                              handleLogout();
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-3"
                          >
                            <LogOut className="w-4 h-4" />
                            Logout
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <Button
                onClick={() => setShowAuthModal(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {t('loginSignup')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Market Alert OR Top Trader Performers Section */}
      {/* When market alert is active, it REPLACES the Top Traders section for better visibility */}
      {marketAlert.enabled ? (
        <div className="max-w-2xl mx-auto mb-4">
          <div 
            className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border-2 border-red-500/50 rounded-xl p-4 animate-pulse"
            data-testid="market-alert-banner"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-14 h-14 bg-red-500/30 rounded-full flex items-center justify-center animate-bounce">
                <AlertTriangle className="w-7 h-7 text-red-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-red-400 font-bold text-lg">🚨 MARKET ALERT ACTIVE</h3>
                  <span className="text-xs text-red-300 bg-red-500/30 px-2 py-0.5 rounded-full animate-pulse">
                    LIVE
                  </span>
                </div>
                <p className="text-red-200 text-sm leading-relaxed">
                  {marketAlert.message || 'Trading is not recommended at this time due to unusual market conditions.'}
                </p>
                <div className="mt-3 flex items-center gap-2 text-red-400/80 text-xs">
                  <AlertTriangle className="w-3 h-3" />
                  <span>Proceed with extreme caution or wait for safer conditions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : topTraderPerformers.length > 0 && (
        <div className="max-w-2xl mx-auto mb-4">
          <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                <h3 className="text-white font-bold">{t('topPerformers')}</h3>
                <span className="text-xs text-yellow-400 bg-yellow-500/20 px-2 py-0.5 rounded-full">
                  {t('live')}
                </span>
              </div>
              {/* Guide Me Now Button */}
              <button
                onClick={openCoachingModal}
                data-testid="guide-me-now-btn"
                className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-xs font-bold rounded-lg transition-all transform hover:scale-105 shadow-lg shadow-purple-500/25"
              >
                <User className="w-3 h-3" />
                Guide me now
              </button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {topTraderPerformers.map((performer, index) => (
                <div 
                  key={index}
                  className="flex-shrink-0 bg-white/5 border border-white/10 rounded-lg p-3 min-w-[140px] hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                      index === 0 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                      index === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-500' :
                      index === 2 ? 'bg-gradient-to-r from-orange-700 to-orange-800' :
                      'bg-gradient-to-r from-blue-500 to-purple-500'
                    }`}>
                      {index < 3 ? ['🥇', '🥈', '🥉'][index] : performer.first_name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{performer.first_name}</p>
                      {performer.country && (
                        <p className="text-gray-500 text-xs">{performer.country}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-center">
                      <p className={`text-lg font-bold ${
                        performer.win_rate >= 70 ? 'text-green-400' :
                        performer.win_rate >= 50 ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>
                        {performer.win_rate}%
                      </p>
                      <p className="text-gray-500 text-xs">{t('winRate')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-blue-400 font-bold">{performer.total_trades}</p>
                      <p className="text-gray-500 text-xs">{t('trades')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-2xl mx-auto">
        {/* Subscription Expired Overlay for Scanner/Generator */}
        {isSubscriptionExpired && guestUser && (
          <div className="mb-6 p-4 bg-gradient-to-r from-red-900/50 to-orange-900/50 border border-red-500/30 rounded-xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Subscription Expired</h3>
                  <p className="text-red-300 text-sm">Renew your subscription to access the signal generator and scanner.</p>
                </div>
              </div>
              <button
                onClick={() => setShowRenewalModal(true)}
                className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold rounded-lg transition-all whitespace-nowrap animate-pulse"
              >
                Renew Now →
              </button>
            </div>
            <p className="text-gray-400 text-xs mt-3">Note: After renewal, it may take up to 30 minutes for your access to be restored.</p>
          </div>
        )}
        
        <Card className={`bg-white/10 backdrop-blur-xl border-white/20 ${isSubscriptionExpired ? 'opacity-50 pointer-events-none relative' : ''}`}>
          {/* Disabled Overlay */}
          {isSubscriptionExpired && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-10 rounded-xl flex flex-col items-center justify-center">
              <div className="p-4 bg-slate-800/90 rounded-xl border border-slate-600 text-center max-w-sm mx-4">
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <h3 className="text-white font-bold text-lg mb-2">Feature Locked</h3>
                <p className="text-gray-300 text-sm mb-4">Your subscription has expired. Renew to unlock the signal generator.</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowRenewalModal(true);
                  }}
                  className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold rounded-lg transition-all pointer-events-auto"
                >
                  Renew Subscription
                </button>
              </div>
            </div>
          )}
          
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5" />
              {t('manualSignalGenerator')}
            </CardTitle>
            <CardDescription className="text-gray-300">
              {t('selectPairDescription')}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Cooling Period Lock Warning for Auto Scanner */}
            {coolingUntil && new Date() < coolingUntil && autoScannerEnabled && guestUser && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-5 h-5 text-amber-400 animate-pulse" />
                  <p className="text-lg font-bold text-amber-400">
                    Auto Scanner Paused - Cooling Down
                  </p>
                </div>
                <p className="text-sm text-gray-300">
                  Scanner will resume automatically at {coolingUntil.toLocaleTimeString()}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  ⏱️ {Math.ceil((coolingUntil - new Date()) / 60000)} minutes remaining
                </p>
              </div>
            )}
            
            {/* Auto Scanner Active Notice */}
            {autoScannerEnabled && guestUser && (!coolingUntil || new Date() >= coolingUntil) && (
              <div className="p-4 bg-gradient-to-r from-blue-600/30 to-purple-600/30 rounded-lg border border-blue-400/50 mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-3 h-3 rounded-full ${autoScannerRunning ? 'bg-blue-500 animate-pulse' : awaitingTradeReport ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
                  <p className="text-lg font-bold text-blue-300">
                    {autoScannerRunning 
                      ? (signalMethod === 'udu-dud' ? 'Scanning for Patterns...' : 'Scanning All Assets...') 
                      : awaitingTradeReport ? 'Signal Found - Report Trade' : 'Auto Scanner Active'}
                  </p>
                </div>
                <p className="text-sm text-gray-300 mb-3">
                  {autoScannerRunning 
                    ? (signalMethod === 'udu-dud' 
                        ? (analyzeStatus?.includes('Reading candle') || analyzeStatus?.includes('Waiting for candle')
                            ? analyzeStatus
                            : 'Looking for UDU-DUD reversal patterns...')
                        : 'Scanning for high-quality signals...')
                    : awaitingTradeReport 
                      ? 'Report your trade result (Win/Loss/Skip) to scan for the next signal'
                      : 'Ready to scan all assets'
                  }
                </p>
                
                {/* Scanning Spinner */}
                {autoScannerRunning && (
                  <div className="p-4 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-400/30 mb-3">
                    <div className="flex flex-col items-center justify-center gap-3">
                      {/* Animated Spinner */}
                      <div className="relative">
                        <div className="w-16 h-16 border-4 border-blue-500/30 rounded-full"></div>
                        <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-cyan-400 rounded-full animate-spin"></div>
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-2xl">
                          {signalMethod === 'udu-dud' ? '🕯️' : '🔍'}
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-cyan-400">
                          {signalMethod === 'udu-dud' 
                            ? 'Scanning for UDU-DUD Patterns' 
                            : `Scanning ${highPayoutOnly ? 'High Payout' : 'All'} Assets`
                          }
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {signalMethod === 'udu-dud'
                            ? `Looking for UD/DU formations in ${assetsScanned}/${highPayoutOnly ? highPayoutAssets.length : manualPairs.length} pairs`
                            : `Analyzing ${assetsScanned}/${highPayoutOnly ? highPayoutAssets.length : manualPairs.length} pairs`
                          }
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {signalMethod === 'udu-dud' 
                            ? '🕯️ Method: UDU-DUD (2-Candle Reversal)'
                            : `🛡️ Anti-Volatility: ${antiVolatilityEnabled ? 'ON' : 'OFF'}`
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* SIGNAL FOUND CARD - Display when signal is found */}
                {bestSignal && awaitingTradeReport && (
                  <div className={`p-4 rounded-lg border-2 ${
                    bestSignal.signal === 'BUY' || bestSignal.signal === 'CALL'
                      ? 'bg-gradient-to-r from-green-600/40 to-emerald-600/40 border-green-400' 
                      : 'bg-gradient-to-r from-red-600/40 to-rose-600/40 border-red-400'
                  }`}>
                    {/* Signal Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`text-4xl p-2 rounded-lg ${
                          bestSignal.signal === 'BUY' || bestSignal.signal === 'CALL' ? 'bg-green-500/30' : 'bg-red-500/30'
                        }`}>
                          {(bestSignal.indicators?.method === 'UDU-DUD' || bestSignal.signal_method === 'udu-dud' || signalMethod === 'udu-dud') ? '🕯️' : (bestSignal.signal === 'BUY' || bestSignal.signal === 'CALL' ? '📈' : '📉')}
                        </div>
                        <div>
                          <p className="text-xl font-black text-white">
                            {bestSignal.signal} SIGNAL
                          </p>
                          <p className="text-sm text-gray-300">
                            {formatAssetName(bestSignal.asset?.name, bestSignal.asset?.symbol, { removeSuffix: true })}
                          </p>
                          {/* Method indicator */}
                          <p className={`text-xs mt-1 ${(bestSignal.indicators?.method === 'UDU-DUD' || bestSignal.signal_method === 'udu-dud' || signalMethod === 'udu-dud') ? 'text-amber-400' : 'text-blue-400'}`}>
                            {(bestSignal.indicators?.method === 'UDU-DUD' || bestSignal.signal_method === 'udu-dud' || signalMethod === 'udu-dud')
                              ? `🕯️ ${bestSignal.indicators?.formation || 'UDU-DUD'} → ${bestSignal.indicators?.expected_next || ''} Pattern`
                              : '📊 MACD • RSI • STOCH'
                            }
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-black text-white">{bestSignal.accuracy}%</p>
                        <p className="text-xs text-gray-400">confidence</p>
                      </div>
                    </div>
                    
                    {/* Entry Countdown Timer */}
                    {entryCountdown !== null && entryCountdown > 0 && (
                      <div className="mb-4 p-3 bg-black/40 rounded-lg border border-cyan-500/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">⏰</span>
                            <div>
                              <p className="text-sm font-bold text-cyan-300">ENTER TRADE IN</p>
                              <p className="text-xs text-gray-400">Wait for the next 1-minute candle</p>
                            </div>
                          </div>
                          <div className="text-4xl font-black text-cyan-400 animate-pulse">
                            {entryCountdown}s
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Execute NOW indicator */}
                    {entryCountdown === 0 && (
                      <div className="mb-4 p-3 bg-yellow-500/30 rounded-lg border-2 border-yellow-400 animate-pulse">
                        <p className="text-lg font-black text-yellow-300 text-center">
                          ⚡ EXECUTE NOW! ⚡
                        </p>
                      </div>
                    )}
                    
                    {/* UDU-DUD Formation Alert */}
                    {(bestSignal.indicators?.method === 'UDU-DUD' || bestSignal.signal_method === 'udu-dud' || signalMethod === 'udu-dud') && bestSignal.indicators?.formation && (
                      <div className="mb-4 p-3 bg-amber-500/30 rounded-lg border border-amber-500/50">
                        <p className="text-sm font-bold text-amber-300 text-center">
                          🕯️ {bestSignal.indicators.formation} Formation → Expecting {bestSignal.indicators.expected_next}
                        </p>
                        <p className="text-xs text-amber-200/80 text-center mt-1">
                          Retracement: {bestSignal.indicators.retracement}% | {bestSignal.indicators.confidence} confidence
                        </p>
                      </div>
                    )}
                    
                    {/* All Indicators Agree Alert (only for traditional method) */}
                    {bestSignal.all_agree && bestSignal.indicators?.method !== 'UDU-DUD' && bestSignal.signal_method !== 'udu-dud' && signalMethod !== 'udu-dud' && (
                      <div className="mb-4 p-3 bg-yellow-500/30 rounded-lg border border-yellow-500/50">
                        <p className="text-sm font-bold text-yellow-300 text-center">
                          🔥🔥🔥 ALL 3 INDICATORS AGREE! HOT SIGNAL! 🔥🔥🔥
                        </p>
                      </div>
                    )}
                    
                    {/* Signal Details Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="p-3 bg-black/30 rounded-lg">
                        <p className="text-xs text-gray-400">Asset</p>
                        <p className="text-lg font-bold text-white">
                          {formatAssetName(bestSignal.asset?.name, bestSignal.asset?.symbol, { removeSuffix: true })}
                        </p>
                      </div>
                      <div className="p-3 bg-black/30 rounded-lg">
                        <p className="text-xs text-gray-400">Payout</p>
                        <p className="text-lg font-bold text-green-400">{bestSignal.asset?.payout || 92}%</p>
                      </div>
                    </div>
                    
                    {/* Signal Strength - Different display for UDU-DUD */}
                    <div className="mb-4 p-3 bg-black/30 rounded-lg">
                      {(bestSignal.indicators?.method === 'UDU-DUD' || bestSignal.signal_method === 'udu-dud' || signalMethod === 'udu-dud') ? (
                        <>
                          <p className="text-xs text-gray-400 mb-2">Pattern Confidence</p>
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">🕯️</span>
                            <div>
                              <p className="text-sm font-bold text-amber-400">{bestSignal.indicators?.pattern || 'UDU-DUD'} Pattern</p>
                              <p className="text-xs text-gray-400">{bestSignal.indicators?.formation || 'N/A'} → {bestSignal.indicators?.expected_next || 'N/A'}</p>
                            </div>
                            <span className={`ml-auto px-2 py-1 rounded text-xs font-bold ${
                              bestSignal.indicators?.confidence === 'HIGH' ? 'bg-green-500/30 text-green-400' :
                              bestSignal.indicators?.confidence === 'MEDIUM-HIGH' ? 'bg-yellow-500/30 text-yellow-400' :
                              'bg-orange-500/30 text-orange-400'
                            }`}>
                              {bestSignal.indicators?.confidence || 'MEDIUM'}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-gray-400 mb-2">Signal Strength ({bestSignal.signal_strength_level}/3 indicators agree)</p>
                          <div className="flex gap-2">
                            {[1, 2, 3].map(level => (
                              <div 
                                key={level}
                                className={`flex-1 h-3 rounded ${
                                  level <= bestSignal.signal_strength_level 
                                    ? 'bg-gradient-to-r from-green-500 to-emerald-400' 
                                    : 'bg-gray-700'
                                }`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* AV Warnings if any */}
                    {bestSignal.av_warnings && bestSignal.av_warnings.length > 0 && (
                      <div className="mb-4 p-3 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                        <p className="text-xs text-yellow-400">⚠️ {bestSignal.av_warnings[0]}</p>
                      </div>
                    )}
                    
                    {/* Recommended Expiry */}
                    {bestSignal.recommended_expiry && (
                      <div className="mb-4 p-2 bg-purple-500/20 rounded-lg border border-purple-500/30">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-purple-300">⏱️ Recommended Expiry</span>
                          <span className="text-sm font-bold text-purple-400">{bestSignal.recommended_expiry?.time || '1m'}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Trade Report Buttons */}
                    <div className="p-3 bg-black/40 rounded-lg border border-white/10">
                      <p className="text-xs text-gray-400 text-center mb-2">After executing trade, report result:</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleTradeReported('win')}
                          className="py-3 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-lg transition-colors"
                        >
                          ✅ WIN
                        </button>
                        <button
                          onClick={() => handleTradeReported('loss')}
                          className="py-3 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-lg transition-colors"
                        >
                          ❌ LOSS
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <button
                          onClick={() => handleTradeReported('low_payout')}
                          className="py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-1"
                        >
                          💰 Low Payout
                          <span className="text-[10px] opacity-75">(Block 15m)</span>
                        </button>
                        <button
                          onClick={() => handleTradeReported('skip')}
                          className="py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded transition-colors"
                        >
                          Skip & Continue
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Turn Off Button */}
                <button
                  onClick={() => {
                    setAutoScannerEnabled(false);
                    setAwaitingTradeReport(false);
                    setBestSignal(null);
                    setLastManualSignalResult(null);
                    setScanRound(0);
                    setEntryCountdown(null);
                  }}
                  className="mt-3 w-full py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded transition-colors"
                >
                  Turn Off Auto Scanner
                </button>
              </div>
            )}
            
            {/* Manual Controls - Hidden when Auto Scanner is active */}
            {(!autoScannerEnabled || !guestUser) && (
            <>
            {/* Pair Selection with Search */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-200">{t('selectTradingPair')}</label>
              
              {/* Search Input */}
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search pairs (e.g., EUR, BTC, GOLD)..."
                  value={pairSearch}
                  onChange={(e) => setPairSearch(e.target.value)}
                  className="pl-9 pr-8 bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500"
                />
                {pairSearch && (
                  <button
                    onClick={() => setPairSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Pair Dropdown */}
              <Select value={selectedManualPair} onValueChange={setSelectedManualPair}>
                <SelectTrigger className="w-full bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select a pair..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] bg-slate-800 border-slate-700">
                  {pairSearch ? (
                    // Show filtered results when searching
                    filteredPairs.length > 0 ? (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-400">
                          🔍 Found {filteredPairs.length} pair{filteredPairs.length !== 1 ? 's' : ''}
                        </div>
                        {filteredPairs.map((pair) => (
                          <SelectItem 
                            key={pair.symbol} 
                            value={pair.symbol} 
                            className="text-white hover:bg-blue-900/30"
                          >
                            <span className="flex items-center gap-2">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-white/10">
                                {pair.type === 'forex' ? '💱' : pair.type === 'crypto' ? '💎' : pair.type === 'commodity' ? '🥇' : '📈'}
                              </span>
                              {pair.name}
                            </span>
                          </SelectItem>
                        ))}
                      </>
                    ) : (
                      <div className="px-3 py-4 text-center text-gray-400">
                        <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>{t('noPairsFound')} &quot;{pairSearch}&quot;</p>
                        <p className="text-xs mt-1">{t('trySearchTerms')}</p>
                      </div>
                    )
                  ) : (
                    // Show categorized list when not searching with payouts
                    <>
                      <div className="px-2 py-1 text-xs font-semibold text-blue-300 bg-blue-900/30">💱 FOREX</div>
                      {manualPairs.filter(p => p.type === 'forex').sort((a,b) => (b.payout || 0) - (a.payout || 0)).map((pair) => (
                        <SelectItem key={pair.symbol} value={pair.symbol} className="text-white hover:bg-blue-900/30">
                          <span className="flex justify-between w-full">
                            <span>{pair.name}</span>
                            {pair.payout && <span className={`ml-2 text-xs ${pair.payout >= 90 ? 'text-green-400' : pair.payout >= 80 ? 'text-yellow-400' : 'text-gray-400'}`}>{pair.payout}%</span>}
                          </span>
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs font-semibold text-purple-300 bg-purple-900/30">💎 CRYPTO</div>
                      {manualPairs.filter(p => p.type === 'crypto').sort((a,b) => (b.payout || 0) - (a.payout || 0)).map((pair) => (
                        <SelectItem key={pair.symbol} value={pair.symbol} className="text-white hover:bg-purple-900/30">
                          <span className="flex justify-between w-full">
                            <span>{pair.name}</span>
                            {pair.payout && <span className={`ml-2 text-xs ${pair.payout >= 90 ? 'text-green-400' : pair.payout >= 80 ? 'text-yellow-400' : 'text-gray-400'}`}>{pair.payout}%</span>}
                          </span>
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs font-semibold text-yellow-300 bg-yellow-900/30">🥇 COMMODITIES</div>
                      {manualPairs.filter(p => p.type === 'commodity').sort((a,b) => (b.payout || 0) - (a.payout || 0)).map((pair) => (
                        <SelectItem key={pair.symbol} value={pair.symbol} className="text-white hover:bg-yellow-900/30">
                          <span className="flex justify-between w-full">
                            <span>{pair.name}</span>
                            {pair.payout && <span className={`ml-2 text-xs ${pair.payout >= 90 ? 'text-green-400' : pair.payout >= 80 ? 'text-yellow-400' : 'text-gray-400'}`}>{pair.payout}%</span>}
                          </span>
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs font-semibold text-green-300 bg-green-900/30">📈 STOCKS</div>
                      {manualPairs.filter(p => p.type === 'stock').sort((a,b) => (b.payout || 0) - (a.payout || 0)).map((pair) => (
                        <SelectItem key={pair.symbol} value={pair.symbol} className="text-white hover:bg-green-900/30">
                          <span className="flex justify-between w-full">
                            <span>{pair.name}</span>
                            {pair.payout && <span className={`ml-2 text-xs ${pair.payout >= 90 ? 'text-green-400' : pair.payout >= 80 ? 'text-yellow-400' : 'text-gray-400'}`}>{pair.payout}%</span>}
                          </span>
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs font-semibold text-cyan-300 bg-cyan-900/30">📊 INDICES</div>
                      {manualPairs.filter(p => p.type === 'index').sort((a,b) => (b.payout || 0) - (a.payout || 0)).map((pair) => (
                        <SelectItem key={pair.symbol} value={pair.symbol} className="text-white hover:bg-cyan-900/30">
                          <span className="flex justify-between w-full">
                            <span>{pair.name}</span>
                            {pair.payout && <span className={`ml-2 text-xs ${pair.payout >= 90 ? 'text-green-400' : pair.payout >= 80 ? 'text-yellow-400' : 'text-gray-400'}`}>{pair.payout}%</span>}
                          </span>
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Indicator Selection - Only visible when signed in */}
            {guestUser && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-200">{t('selectIndicators')}</label>
                <Select value={selectedIndicators} onValueChange={setSelectedIndicators}>
                  <SelectTrigger className="w-full bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Select indicator..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white hover:bg-purple-900/30">
                      <div className="flex items-center gap-2">
                        <span>🎯</span>
                        <span>{t('allIndicatorsMacdStochRsi')}</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="macd" className="text-white hover:bg-blue-900/30">
                      <div className="flex items-center gap-2">
                        <span>📊</span>
                        <span>{t('macdOnly')}</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="stochastic" className="text-white hover:bg-green-900/30">
                      <div className="flex items-center gap-2">
                        <span>📈</span>
                        <span>{t('stochasticOnly')}</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="rsi" className="text-white hover:bg-yellow-900/30">
                      <div className="flex items-center gap-2">
                        <span>⚡</span>
                        <span>{t('rsiPeriod7Only')}</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400">
                  {selectedIndicators === 'all' 
                    ? t('usingAllIndicators')
                    : selectedIndicators === 'macd'
                      ? t('macdDesc')
                      : selectedIndicators === 'stochastic'
                        ? t('stochDesc')
                        : t('rsiDesc')}
                </p>
              </div>
            )}

            {/* Anti-Volatility Protection Toggle - Only visible when signed in */}
            {guestUser && (
              <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🛡️</span>
                    <div>
                      <p className="text-sm font-bold text-white">Anti-Volatility Protection</p>
                      <p className="text-xs text-gray-400">6-layer protection against volatile markets</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAntiVolatilityEnabled(!antiVolatilityEnabled)}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                      antiVolatilityEnabled ? 'bg-green-600' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        antiVolatilityEnabled ? 'translate-x-8' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {/* Protection Features List */}
                {antiVolatilityEnabled && (
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div className="flex items-center gap-1.5 text-xs text-green-400">
                      <span>✓</span> ATR Volatility Check
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-green-400">
                      <span>✓</span> Volume Confirmation
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-green-400">
                      <span>✓</span> Candle Pattern Filter
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-green-400">
                      <span>✓</span> Session Time Filter
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-green-400">
                      <span>✓</span> Loss Protection
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-green-400">
                      <span>✓</span> Strict Multi-TF
                    </div>
                  </div>
                )}
                
                {/* Show AV Check Results when available */}
                {avCheckResults && antiVolatilityEnabled && (
                  <div className="mt-3 p-2 bg-black/30 rounded-lg">
                    <p className="text-xs font-medium text-white mb-2">Last Check Results:</p>
                    <div className="space-y-1">
                      {avCheckResults.volatility && (
                        <div className={`text-xs ${avCheckResults.volatility.is_safe ? 'text-green-400' : 'text-red-400'}`}>
                          {avCheckResults.volatility.is_safe ? '✓' : '✗'} Volatility: {avCheckResults.volatility.volatility_level}
                        </div>
                      )}
                      {avCheckResults.volume && (
                        <div className={`text-xs ${avCheckResults.volume.is_valid ? 'text-green-400' : 'text-red-400'}`}>
                          {avCheckResults.volume.is_valid ? '✓' : '✗'} Volume: {avCheckResults.volume.volume_ratio}x avg
                        </div>
                      )}
                      {avCheckResults.candle_patterns && (
                        <div className={`text-xs ${avCheckResults.candle_patterns.is_clean ? 'text-green-400' : 'text-red-400'}`}>
                          {avCheckResults.candle_patterns.is_clean ? '✓' : '✗'} Candles: {avCheckResults.candle_patterns.is_clean ? 'Clean' : 'Bad patterns'}
                        </div>
                      )}
                      {avCheckResults.session_time && (
                        <div className={`text-xs ${avCheckResults.session_time.is_safe ? 'text-green-400' : 'text-red-400'}`}>
                          {avCheckResults.session_time.is_safe ? '✓' : '✗'} Session: {avCheckResults.session_time.is_safe ? 'Safe' : 'Dangerous'}
                        </div>
                      )}
                      {avCheckResults.loss_protection && (
                        <div className={`text-xs ${avCheckResults.loss_protection.can_trade ? 'text-green-400' : 'text-red-400'}`}>
                          {avCheckResults.loss_protection.can_trade ? '✓' : '✗'} Loss Protection: {
                            avCheckResults.loss_protection.can_trade 
                              ? 'OK' 
                              : `Paused (${avCheckResults.loss_protection.consecutive_losses} losses)`
                          }
                        </div>
                      )}
                      {avCheckResults.multi_timeframe && (
                        <div className={`text-xs ${avCheckResults.multi_timeframe.all_agree ? 'text-green-400' : 'text-red-400'}`}>
                          {avCheckResults.multi_timeframe.all_agree ? '✓' : '✗'} Timeframes: {avCheckResults.multi_timeframe.aligned_count}/3
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Signal Timeframe Selector - Only visible when signed in */}
            {guestUser && (
              <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">⏱️</span>
                  <div>
                    <p className="text-sm font-medium text-white">Signal Timeframe & Expiry</p>
                    <p className="text-xs text-gray-400">Select timeframe - expiry will match automatically</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: '1', label: '1M', expiry: '1 min' },
                    { value: '2', label: '2M', expiry: '2 min' },
                    { value: '5', label: '5M', expiry: '5 min' },
                    { value: '15', label: '15M', expiry: '15 min' },
                    { value: '60', label: '1H', expiry: '1 hour' }
                  ].map((tf) => (
                    <button
                      key={tf.value}
                      onClick={() => setSignalTimeframe(tf.value)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        signalTimeframe === tf.value
                          ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                      data-testid={`timeframe-${tf.value}`}
                    >
                      <div className="flex flex-col items-center">
                        <span>{tf.label}</span>
                        {signalTimeframe === tf.value && (
                          <span className="text-xs text-blue-200">({tf.expiry})</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                {/* Current Selection Indicator */}
                <div className="mt-3 p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-xs text-blue-300 text-center">
                    🔗 Expiry synced: <span className="font-bold text-blue-400">
                      {signalTimeframe === '1' ? '1 minute' : 
                       signalTimeframe === '2' ? '2 minutes' : 
                       signalTimeframe === '5' ? '5 minutes' : 
                       signalTimeframe === '15' ? '15 minutes' : 
                       signalTimeframe === '60' ? '1 hour' : `${signalTimeframe} min`}
                    </span>
                  </p>
                </div>
              </div>
            )}

            {/* Signal Method Toggle - UDU-DUD vs Traditional Indicators */}
            {guestUser && (
              <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🔀</span>
                  <div>
                    <p className="text-sm font-medium text-white">Signal Method</p>
                    <p className="text-xs text-gray-400">Choose your signal detection strategy</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setSignalMethod('indicators')}
                    data-testid="method-indicators"
                    className={`p-3 rounded-lg text-sm font-medium transition-all border ${
                      signalMethod === 'indicators'
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white border-blue-400 ring-2 ring-blue-400/50'
                        : 'bg-white/10 text-gray-300 border-white/10 hover:bg-white/20'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-lg">📊</span>
                      <span className="font-bold">Indicators</span>
                      <span className="text-xs opacity-70">MACD • RSI • STOCH</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setSignalMethod('udu-dud')}
                    data-testid="method-udu-dud"
                    className={`p-3 rounded-lg text-sm font-medium transition-all border ${
                      signalMethod === 'udu-dud'
                        ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white border-amber-400 ring-2 ring-amber-400/50'
                        : 'bg-white/10 text-gray-300 border-white/10 hover:bg-white/20'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-lg">🕯️</span>
                      <span className="font-bold">UDU-DUD</span>
                      <span className="text-xs opacity-70">2-Candle Reversal</span>
                    </div>
                  </button>
                </div>
                {/* Method Description */}
                <div className={`mt-3 p-2 rounded-lg border ${
                  signalMethod === 'udu-dud' 
                    ? 'bg-amber-500/10 border-amber-500/30' 
                    : 'bg-blue-500/10 border-blue-500/30'
                }`}>
                  {signalMethod === 'udu-dud' ? (
                    <div className="text-xs text-amber-300">
                      <p className="font-bold mb-1">🕯️ UDU-DUD Method:</p>
                      <p>• <span className="text-green-400">DUD (GREEN→RED)</span> = CALL signal</p>
                      <p>• <span className="text-red-400">UDU (RED→GREEN)</span> = PUT signal</p>
                      <p className="mt-1 text-amber-400/80">Requires 70%+ retracement & aligned candles</p>
                    </div>
                  ) : (
                    <div className="text-xs text-blue-300">
                      <p className="font-bold mb-1">📊 Traditional Indicators:</p>
                      <p>• MACD crossover analysis</p>
                      <p>• RSI overbought/oversold</p>
                      <p>• Stochastic momentum</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Auto Scanner Section - Only visible when signed in */}
            {guestUser && (
              <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{autoScannerRunning ? '🔄' : '🔍'}</span>
                    <div>
                      <p className="text-sm font-medium text-white">
                        {t('autoScanner') || 'Auto Scanner'}
                        {autoScannerRunning && <span className="ml-2 text-xs text-blue-400">(Running)</span>}
                      </p>
                      <p className="text-xs text-gray-400">{t('autoScannerDesc') || 'Automatically scan multiple assets for signals'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="auto-scanner-toggle"
                      onClick={() => {
                        setAutoScannerEnabled(!autoScannerEnabled);
                        if (autoScannerEnabled) {
                          setAwaitingTradeReport(false);
                          setBestSignal(null);
                          setScanResults([]);
                          setScanComplete(false);
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        autoScannerEnabled ? 'bg-blue-600' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          autoScannerEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                
                {/* High Payout Filter Toggle - Only show when Auto Scanner is enabled */}
                {autoScannerEnabled && (
                  <div className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-yellow-400">💰</span>
                      <div>
                        <p className="text-xs font-medium text-white">{t('highPayoutFilter') || 'High Payout Only (92%+)'}</p>
                        <p className="text-[10px] text-gray-500">{t('highPayoutFilterDesc') || 'Scan only high-payout assets'}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setHighPayoutOnly(!highPayoutOnly)}
                      disabled={autoScannerRunning}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        highPayoutOnly ? 'bg-yellow-500' : 'bg-gray-600'
                      } ${autoScannerRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                          highPayoutOnly ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                )}
                
                {/* Blocked Low Payout Assets Display */}
                {autoScannerEnabled && blockedLowPayoutAssets.length > 0 && (
                  <div className="p-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-orange-400">
                        🚫 Blocked Assets ({blockedLowPayoutAssets.length})
                      </p>
                      <button
                        onClick={() => {
                          setBlockedLowPayoutAssets([]);
                          localStorage.removeItem('blockedLowPayoutAssets');
                          toast.info('All blocked assets cleared');
                        }}
                        className="text-[10px] text-orange-300 hover:text-orange-100 underline"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {blockedLowPayoutAssets.map((item, idx) => {
                        const remainingMs = item.expiresAt - Date.now();
                        const remainingMins = Math.max(0, Math.ceil(remainingMs / 60000));
                        return (
                          <span 
                            key={idx}
                            className="px-2 py-0.5 text-[10px] bg-orange-500/20 text-orange-300 rounded-full"
                          >
                            {item.name?.replace(' OTC', '') || item.symbol} ({remainingMins}m)
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Generate Button */}
            {guestUser ? (
              pendingReport ? (
                /* Pending Trade Report UI */
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-300 font-medium text-center mb-3">
                      {t('reportBeforeNew')}
                    </p>
                    {lastSignalInfo && (
                      <p className="text-yellow-200/70 text-sm text-center mb-4">
                        {t('lastSignal')}: {lastSignalInfo.signal} {t('on')} {formatAssetName(lastSignalInfo.asset_name, lastSignalInfo.symbol, { removeSuffix: true })}
                      </p>
                    )}
                    <div className="flex gap-2 justify-center flex-wrap">
                      <Button
                        onClick={() => reportTradeResult('win')}
                        disabled={reportingResult}
                        className="bg-green-600 hover:bg-green-700 text-white px-6"
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        {t('win')}
                      </Button>
                      <Button
                        onClick={skipSignal}
                        disabled={reportingResult}
                        variant="outline"
                        className="border-gray-500 text-gray-300 hover:bg-gray-700 px-6"
                      >
                        <SkipForward className="w-5 h-5 mr-2" />
                        {t('skip')}
                      </Button>
                      <Button
                        onClick={() => reportTradeResult('loss')}
                        disabled={reportingResult}
                        className="bg-red-600 hover:bg-red-700 text-white px-6"
                      >
                        <XCircle className="w-5 h-5 mr-2" />
                        {t('loss')}
                      </Button>
                      <Button
                        onClick={() => reportTradeResult('low_payout')}
                        disabled={reportingResult}
                        className="bg-orange-600 hover:bg-orange-700 text-white px-4 text-xs"
                      >
                        💰 Low Payout
                      </Button>
                    </div>
                    <p className="text-gray-400 text-xs text-center mt-3">
                      {t('skipIfNotTraded')} • Low Payout blocks asset for 15 mins
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Tip Text */}
                  <div className="mb-4 p-3 bg-gradient-to-r from-yellow-900/30 to-orange-900/30 rounded-lg border border-yellow-500/30">
                    <p className="text-yellow-300 text-sm text-center font-medium">
                      💡 {t('proTip')}: Wait for the timer countdown to enter the trade
                    </p>
                  </div>
                  
                  {/* Analyzing Progress UI */}
                  {analyzingSignal && (
                    <div className="mb-4 p-4 bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-xl border border-blue-500/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-blue-300 font-medium flex items-center gap-2">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          {signalMethod === 'udu-dud' ? '🕯️ Analyzing Candle Pattern' : t('analyzingMarket')}
                        </span>
                        <span className="text-blue-400 font-mono text-sm">{Math.round(analyzeProgress)}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-100 ease-linear ${
                            signalMethod === 'udu-dud' 
                              ? 'bg-gradient-to-r from-amber-500 to-orange-500' 
                              : 'bg-gradient-to-r from-blue-500 to-purple-500'
                          }`}
                          style={{ width: `${analyzeProgress}%` }}
                        ></div>
                      </div>
                      
                      {/* Method-specific analysis steps */}
                      {signalMethod === 'udu-dud' ? (
                        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                          <div className={`p-2 rounded ${analyzeProgress > 20 ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-700 text-gray-500'}`}>
                            ✓ Reading Candles
                          </div>
                          <div className={`p-2 rounded ${analyzeProgress > 50 ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-700 text-gray-500'}`}>
                            ✓ Detecting Pattern
                          </div>
                          <div className={`p-2 rounded ${analyzeProgress > 80 ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-700 text-gray-500'}`}>
                            ✓ Checking Retracement
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                          <div className={`p-2 rounded ${analyzeProgress > 20 ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
                            ✓ {t('macdAnalysis')}
                          </div>
                          <div className={`p-2 rounded ${analyzeProgress > 50 ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
                            ✓ {t('rsiAnalysis')}
                          </div>
                          <div className={`p-2 rounded ${analyzeProgress > 80 ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
                            ✓ {t('stochasticAnalysis')}
                          </div>
                        </div>
                      )}
                      
                      {/* Status message */}
                      {analyzeStatus && (
                        <div className={`mt-2 p-2 rounded border ${
                          signalMethod === 'udu-dud'
                            ? 'bg-amber-500/10 border-amber-500/20'
                            : 'bg-yellow-500/10 border-yellow-500/20'
                        }`}>
                          <p className={`text-xs text-center animate-pulse ${
                            signalMethod === 'udu-dud' ? 'text-amber-300' : 'text-yellow-300'
                          }`}>
                            {signalMethod === 'udu-dud' ? '🕯️' : '🔍'} {analyzeStatus}
                          </p>
                        </div>
                      )}
                      <p className="text-gray-400 text-xs mt-2 text-center">
                        {signalMethod === 'udu-dud' 
                          ? 'Looking for UD or DU formation with 70%+ retracement...'
                          : t('scanningBestEntry')
                        }
                      </p>
                    </div>
                  )}
                  
                  {/* Cooling Period Lock Warning */}
                  {coolingUntil && new Date() < coolingUntil && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
                      <div className="flex items-center gap-2 text-amber-400">
                        <Clock className="w-4 h-4 animate-pulse" />
                        <span className="text-sm font-medium">Cooling Period - Trading Locked</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Resumes at: {coolingUntil.toLocaleTimeString()} ({Math.ceil((coolingUntil - new Date()) / 60000)} min left)
                      </p>
                    </div>
                  )}
                  
                  <Button 
                    onClick={generateManualSignal}
                    disabled={generatingSignal || analyzingSignal || (coolingUntil && new Date() < coolingUntil)}
                    className={`w-full font-bold py-6 text-lg ${
                      coolingUntil && new Date() < coolingUntil 
                        ? 'bg-gray-600 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                    } text-white`}
                  >
                    {coolingUntil && new Date() < coolingUntil ? (
                      <>
                        <Clock className="w-5 h-5 mr-2" />
                        Cooling Down...
                      </>
                    ) : analyzingSignal ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        {t('analyzingMarketSecs')} ({Math.round(15 - (analyzeProgress / 100 * 15))}s)
                      </>
                    ) : generatingSignal ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        {t('generatingSignal')}
                      </>
                    ) : (
                      <>
                        <Activity className="w-5 h-5 mr-2" />
                        {t('generateSignalFor')} {selectedManualPair.replace('_OTC', '')}
                      </>
                    )}
                  </Button>
                </>
              )
            ) : (
              <div className="space-y-3">
                <Button 
                  onClick={() => setShowAuthModal(true)}
                  className="w-full bg-gradient-to-r from-gray-500 to-gray-600 hover:from-blue-600 hover:to-purple-600 text-white font-bold py-6 text-lg"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  {t('loginToGenerateSignal')}
                </Button>
                <p className="text-xs text-center text-gray-400">
                  {t('signUpToAccess')}
                </p>
              </div>
            )}

            {/* Signal Result */}
            {lastManualSignalResult && (
              <div className={`p-4 rounded-lg ${
                lastManualSignalResult.type === 'success' 
                  ? lastManualSignalResult.signal_strength_level === 3
                    ? 'bg-gradient-to-r from-yellow-500/30 to-orange-500/30 border-2 border-yellow-400 animate-pulse'
                    : lastManualSignalResult.signal === 'BUY' 
                      ? 'bg-green-500/20 border-2 border-green-400' 
                      : 'bg-red-500/20 border-2 border-red-400'
                  : lastManualSignalResult.type === 'blocked'
                    ? 'bg-orange-500/20 border-2 border-orange-400'
                    : lastManualSignalResult.type === 'info'
                      ? 'bg-blue-500/20 border border-blue-500'
                      : 'bg-red-500/20 border border-red-500'
              }`}>
                {/* BLOCKED BY ANTI-VOLATILITY */}
                {lastManualSignalResult.type === 'blocked' && (
                  <div className="space-y-3">
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-center py-3 rounded-lg">
                      <div className="text-2xl mb-1">🛡️ SIGNAL BLOCKED</div>
                      <div className="text-sm">Anti-Volatility Protection Active</div>
                    </div>
                    
                    <div className="bg-black/30 rounded-lg p-3">
                      <p className="text-sm text-orange-300 font-medium mb-2">Blocked Reasons:</p>
                      <p className="text-xs text-gray-300">{lastManualSignalResult.message}</p>
                    </div>
                    
                    <div className="text-center">
                      <p className="text-xs text-gray-400">
                        The market conditions are not safe for trading right now. 
                        Wait for better conditions or try a different asset.
                      </p>
                    </div>
                  </div>
                )}
                
                {lastManualSignalResult.type === 'success' && (
                  <div className="space-y-3">
                    {/* UDU-DUD Formation Banner */}
                    {lastManualSignalResult.signal_method === 'udu-dud' && lastManualSignalResult.indicators?.formation && (
                      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold text-center py-2 rounded-lg mb-2">
                        🕯️ {lastManualSignalResult.indicators.formation} Formation → {lastManualSignalResult.indicators.expected_next} ({lastManualSignalResult.indicators.pattern})
                      </div>
                    )}
                    
                    {/* SUPER STRONG/HOT Signal Banner (Traditional indicators only) */}
                    {lastManualSignalResult.signal_method !== 'udu-dud' && lastManualSignalResult.signal_strength_level === 3 && (
                      <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold text-center py-2 rounded-lg mb-2">
                        {t('hotSignalAll3')}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-3xl font-bold">
                        {(lastManualSignalResult.signal === 'BUY' || lastManualSignalResult.signal === 'CALL') ? '🟢' : '🔴'} 
                        <span className={(lastManualSignalResult.signal === 'BUY' || lastManualSignalResult.signal === 'CALL') ? 'text-green-400' : 'text-red-400'}>
                          {lastManualSignalResult.signal}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${
                          lastManualSignalResult.accuracy >= 90 ? 'text-yellow-400' :
                          lastManualSignalResult.accuracy >= 80 ? 'text-green-400' : 'text-blue-400'
                        }`}>
                          {lastManualSignalResult.accuracy}%
                        </div>
                        <div className="text-xs text-gray-400">{t('accuracy')}</div>
                      </div>
                    </div>
                    
                    {/* Signal Method Badge */}
                    <div className="flex justify-center">
                      {lastManualSignalResult.signal_method === 'udu-dud' ? (
                        <span className="px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-black">
                          🕯️ UDU-DUD: {lastManualSignalResult.indicators?.formation} → {lastManualSignalResult.indicators?.expected_next}
                        </span>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                          lastManualSignalResult.signal_strength_level === 3
                            ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black'
                            : lastManualSignalResult.signal_strength_level === 2
                              ? 'bg-green-500/30 text-green-300 border border-green-500'
                              : 'bg-blue-500/30 text-blue-300 border border-blue-500'
                        }`}>
                          {lastManualSignalResult.signal_strength_level === 3 ? `🔥 ${t('hotSignal')}` :
                           lastManualSignalResult.signal_strength_level === 2 ? `💪 ${t('strongSignal')}` : `📊 ${t('medium')}`}
                        </span>
                      )}
                    </div>
                    
                    {/* UDU-DUD Retracement Info */}
                    {lastManualSignalResult.signal_method === 'udu-dud' && lastManualSignalResult.indicators?.retracement && (
                      <div className="p-3 rounded-lg border bg-amber-500/10 border-amber-500/30">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-amber-300">📏 Retracement</span>
                          <span className="text-lg font-bold text-amber-400">{lastManualSignalResult.indicators.retracement}%</span>
                        </div>
                        <div className="mt-2 text-xs text-amber-200/70">
                          {lastManualSignalResult.indicators.confidence} confidence • Pattern: {lastManualSignalResult.indicators.pattern}
                        </div>
                      </div>
                    )}
                    
                    {/* Dynamic Expiry Recommendation */}
                    {lastManualSignalResult.recommended_expiry && (
                      <div className={`p-3 rounded-lg border ${
                        lastManualSignalResult.recommended_expiry.confidence === 'HIGH'
                          ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/40'
                          : lastManualSignalResult.recommended_expiry.confidence === 'MEDIUM-HIGH'
                            ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-500/40'
                            : 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-500/40'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-300">⏱️ Recommended Expiry</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                            lastManualSignalResult.recommended_expiry.confidence === 'HIGH'
                              ? 'bg-purple-500/30 text-purple-300'
                              : lastManualSignalResult.recommended_expiry.confidence === 'MEDIUM-HIGH'
                                ? 'bg-blue-500/30 text-blue-300'
                                : 'bg-yellow-500/30 text-yellow-300'
                          }`}>
                            {lastManualSignalResult.recommended_expiry.confidence}
                          </span>
                        </div>
                        <div className="text-center">
                          <span className="text-3xl font-black text-white">
                            {lastManualSignalResult.recommended_expiry.time}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 text-center mt-1">
                          💡 {lastManualSignalResult.recommended_expiry.reason}
                        </p>
                      </div>
                    )}
                    
                    {/* Entry Countdown - Next 1M Candle */}
                    {entryCountdown !== null && entryCountdown > 0 && (
                      <div className="bg-gradient-to-r from-blue-600/30 to-purple-600/30 border-2 border-blue-400 rounded-xl p-4 text-center animate-pulse">
                        <p className="text-blue-300 text-sm mb-1">⏰ Enter on next 1M candle in:</p>
                        <div className="text-4xl font-bold text-white">
                          {entryCountdown}s
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Get ready to place your trade!</p>
                      </div>
                    )}
                    {entryCountdown === 0 && (
                      <div className="bg-gradient-to-r from-green-600/30 to-emerald-600/30 border-2 border-green-400 rounded-xl p-4 text-center">
                        <p className="text-green-300 text-lg font-bold">🚀 NOW! Enter the trade!</p>
                      </div>
                    )}
                    
                    <div className="bg-white/10 rounded-lg p-3 space-y-2">
                      <p className="font-semibold text-lg text-white">
                        📊 {formatAssetName(lastManualSignalResult.asset_name, lastManualSignalResult.asset_symbol, { removeSuffix: true })}
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-300">
                        <p>⏱️ {t('expiry')}: <span className="font-bold text-white">{lastManualSignalResult.expiry}</span></p>
                        <p>💰 {t('payout')}: <span className="font-bold text-white">{lastManualSignalResult.payout}%</span></p>
                      </div>
                      
                      {/* UDU-DUD Pattern Details */}
                      {lastManualSignalResult.signal_method === 'udu-dud' && lastManualSignalResult.indicators?.method === 'UDU-DUD' && (
                        <div className="mt-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="p-2 bg-amber-500/20 rounded">
                              <div className="text-xs text-amber-300 font-bold">Pattern</div>
                              <div className="text-lg font-bold text-amber-400">{lastManualSignalResult.indicators.pattern}</div>
                            </div>
                            <div className="p-2 bg-amber-500/20 rounded">
                              <div className="text-xs text-amber-300 font-bold">Formation</div>
                              <div className="text-lg font-bold text-amber-400">{lastManualSignalResult.indicators.formation} → {lastManualSignalResult.indicators.expected_next}</div>
                            </div>
                            <div className="p-2 bg-amber-500/20 rounded">
                              <div className="text-xs text-amber-300 font-bold">Retracement</div>
                              <div className="text-lg font-bold text-amber-400">{lastManualSignalResult.indicators.retracement}%</div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Traditional Indicator Status - Only show for indicators method */}
                      {lastManualSignalResult.signal_method !== 'udu-dud' && lastManualSignalResult.indicators && lastManualSignalResult.indicators.method !== 'UDU-DUD' && (
                        <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                          <div className={`p-2 rounded text-center ${
                            lastManualSignalResult.indicators.macd_signal === lastManualSignalResult.signal 
                              ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                          }`}>
                            <div className="font-bold">MACD</div>
                            <div>{lastManualSignalResult.indicators.macd_signal || 'N/A'}</div>
                          </div>
                          <div className={`p-2 rounded text-center ${
                            lastManualSignalResult.indicators.stoch_signal === lastManualSignalResult.signal 
                              ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                          }`}>
                            <div className="font-bold">STOCH</div>
                            <div>{lastManualSignalResult.indicators.stoch_signal || 'N/A'}</div>
                          </div>
                          <div className={`p-2 rounded text-center ${
                            lastManualSignalResult.indicators.rsi_signal === lastManualSignalResult.signal 
                              ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                          }`}>
                            <div className="font-bold">RSI 7</div>
                            <div>{lastManualSignalResult.indicators.rsi_signal || 'N/A'}</div>
                            <div className="text-[10px] text-gray-400">{lastManualSignalResult.indicators.rsi?.toFixed(1) || 'N/A'}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Anti Manipulation Risk Level */}
                      {lastManualSignalResult.risk_level && (
                        <div className={`mt-3 p-3 rounded-lg border ${
                          lastManualSignalResult.risk_level === 'LOW' 
                            ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/40'
                            : lastManualSignalResult.risk_level === 'MEDIUM'
                              ? 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-500/40'
                              : 'bg-gradient-to-r from-red-500/20 to-orange-500/20 border-red-500/40'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-300 flex items-center gap-1">
                              🛡️ Anti Manipulation Analysis
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                              lastManualSignalResult.risk_level === 'LOW' 
                                ? 'bg-green-500/30 text-green-300'
                                : lastManualSignalResult.risk_level === 'MEDIUM'
                                  ? 'bg-yellow-500/30 text-yellow-300'
                                  : 'bg-red-500/30 text-red-300'
                            }`}>
                              {lastManualSignalResult.risk_level === 'LOW' ? '✓ SAFE' : '⚠️ RISKY'} TRADE
                            </span>
                          </div>
                          
                          {/* Timeframe Alignment */}
                          <div className="grid grid-cols-3 gap-2 mb-2">
                            <div className={`p-2 rounded text-center text-xs ${
                              lastManualSignalResult.timeframe_alignment?.tf_15m === lastManualSignalResult.signal
                                ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                            }`}>
                              <div className="font-bold">15M</div>
                              <div>{lastManualSignalResult.timeframe_alignment?.tf_15m || 'N/A'}</div>
                            </div>
                            <div className={`p-2 rounded text-center text-xs ${
                              lastManualSignalResult.timeframe_alignment?.tf_1h === lastManualSignalResult.signal
                                ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                            }`}>
                              <div className="font-bold">1H</div>
                              <div>{lastManualSignalResult.timeframe_alignment?.tf_1h || 'N/A'}</div>
                            </div>
                            <div className={`p-2 rounded text-center text-xs ${
                              lastManualSignalResult.timeframe_alignment?.tf_4h === lastManualSignalResult.signal
                                ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                            }`}>
                              <div className="font-bold">4H</div>
                              <div>{lastManualSignalResult.timeframe_alignment?.tf_4h || 'N/A'}</div>
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-400 text-center">
                            {lastManualSignalResult.timeframe_alignment?.aligned_count === 3
                              ? '✅ All timeframes aligned - Lower manipulation risk'
                              : lastManualSignalResult.timeframe_alignment?.aligned_count === 2
                                ? '⚠️ 2/3 timeframes aligned - Moderate risk'
                                : lastManualSignalResult.timeframe_alignment?.aligned_count === 1
                                  ? '⚠️ Only 1/3 timeframes aligned - Higher risk'
                                  : '🚨 No timeframe alignment - High manipulation risk'}
                          </p>
                        </div>
                      )}
                      
                      <div className={`mt-2 p-2 rounded text-center ${
                        lastManualSignalResult.signal_strength_level === 3 
                          ? 'bg-gradient-to-r from-yellow-500/30 to-orange-500/30'
                          : 'bg-yellow-500/20'
                      }`}>
                        <p className={`font-bold ${
                          lastManualSignalResult.signal_strength_level === 3 ? 'text-yellow-300' : 'text-yellow-300'
                        }`}>{t('executeNow')}</p>
                      </div>
                      <p className="text-sm text-center text-gray-400 mt-2">🎯 {lastManualSignalResult.reason}</p>
                    </div>
                  </div>
                )}
                {lastManualSignalResult.type === 'info' && (
                  <div className="text-blue-300 text-center">
                    ℹ️ {lastManualSignalResult.message}
                  </div>
                )}
                {lastManualSignalResult.type === 'error' && (
                  <div className="text-red-400">
                    ❌ {lastManualSignalResult.message}
                  </div>
                )}
              </div>
            )}

            {/* Stats - Hidden when Auto Scanner is active */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-white">{manualPairs.length}</div>
                <div className="text-xs text-gray-400">{t('availablePairs')}</div>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-white">{guestUser?.signals_generated || 0}</div>
                <div className="text-xs text-gray-400">{t('yourSignals')}</div>
              </div>
            </div>
            </>
            )}
          </CardContent>
        </Card>

        {/* Profitability Stats Card */}
        {guestUser && profitabilityStats && (
          <Card className="bg-white/10 backdrop-blur-xl border-white/20 mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  {t('tradingStats')}
                </div>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="last_24h" className="text-white">{t('last24h')}</SelectItem>
                    <SelectItem value="yesterday" className="text-white">{t('yesterday')}</SelectItem>
                    <SelectItem value="this_week" className="text-white">{t('thisWeek')}</SelectItem>
                    <SelectItem value="all_time" className="text-white">{t('allTime')}</SelectItem>
                  </SelectContent>
                </Select>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profitabilityStats[selectedPeriod] && (
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-white">{profitabilityStats[selectedPeriod].trades}</div>
                    <div className="text-xs text-gray-400">{t('trades')}</div>
                  </div>
                  <div className="bg-green-500/20 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-400">{profitabilityStats[selectedPeriod].wins}</div>
                    <div className="text-xs text-gray-400">{t('wins')}</div>
                  </div>
                  <div className="bg-red-500/20 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-red-400">{profitabilityStats[selectedPeriod].losses}</div>
                    <div className="text-xs text-gray-400">{t('losses')}</div>
                  </div>
                  <div className={`rounded-lg p-3 text-center ${
                    profitabilityStats[selectedPeriod].win_rate >= 60 ? 'bg-green-500/20' :
                    profitabilityStats[selectedPeriod].win_rate >= 40 ? 'bg-yellow-500/20' : 'bg-red-500/20'
                  }`}>
                    <div className={`text-2xl font-bold ${
                      profitabilityStats[selectedPeriod].win_rate >= 60 ? 'text-green-400' :
                      profitabilityStats[selectedPeriod].win_rate >= 40 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {profitabilityStats[selectedPeriod].win_rate}%
                    </div>
                    <div className="text-xs text-gray-400">{t('winRate')}</div>
                  </div>
                </div>
              )}
              {profitabilityStats[selectedPeriod]?.trades === 0 && (
                <p className="text-center text-gray-400 py-4">{t('noTradesRecorded')}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Policy Sections - Collapsible at bottom */}
        <PolicySections />

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>{t('tradingDisclaimer1')}</p>
          <p>{t('tradingDisclaimer2')}</p>
        </div>
      </div>

      {/* Education TV - Only show for logged in users */}
      {guestUser && (
        <>
          <EducationTVButton 
            notificationCount={educationNotifications}
            onClick={() => setShowEducationTV(true)}
          />
          <EducationTVModal
            isOpen={showEducationTV}
            onClose={() => {
              setShowEducationTV(false);
              // Refresh notification count after closing
              const guestToken = localStorage.getItem('guestToken');
              if (guestToken) {
                fetch(`${API}/education/notifications?token=${guestToken}`)
                  .then(res => res.json())
                  .then(data => {
                    if (data.success) setEducationNotifications(data.unviewed_count || 0);
                  })
                  .catch(() => {});
              }
            }}
            userToken={localStorage.getItem('guestToken')}
            userName={guestUser?.first_name || guestUser?.name?.split(' ')[0] || 'User'}
          />
        </>
      )}

      {/* Referral Modal */}
      {showReferralModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="relative w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl overflow-hidden border border-white/10">
            <button 
              onClick={() => setShowReferralModal(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors z-10"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>

            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">{t('referAndEarn')}</h2>
              <p className="text-white/80 mt-1">{t('get7FreeDays')}</p>
            </div>

            <div className="p-6 space-y-4">
              {!referralInfo ? (
                <div className="text-center py-4">
                  <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-2 animate-spin" />
                  <p className="text-gray-400">{t('loadingReferralInfo')}</p>
                </div>
              ) : !referralInfo.success ? (
                <div className="text-center py-4">
                  <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <p className="text-red-400">{t('sessionExpiredInvalid')}</p>
                  <p className="text-gray-500 text-sm mt-1 mb-4">{referralInfo.message || t('pleaseLogOutIn')}</p>
                  <div className="flex gap-2 justify-center">
                    <Button
                      onClick={async () => {
                        setReferralInfo(null);
                        const guestToken = localStorage.getItem('guestToken');
                        if (guestToken) {
                          try {
                            const response = await fetch(`${API}/user/referral-info?token=${guestToken}`);
                            const data = await response.json();
                            setReferralInfo(data);
                          } catch (err) {
                            setReferralInfo({ success: false, message: err.message });
                          }
                        }
                      }}
                      variant="outline"
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t('retry')}
                    </Button>
                    <Button
                      onClick={() => {
                        localStorage.removeItem('guestToken');
                        localStorage.removeItem('guestUser');
                        window.location.reload();
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('logout')}
                    </Button>
                  </div>
                </div>
              ) : !referralInfo.referral_code ? (
                <div className="text-center py-4">
                  <XCircle className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                  <p className="text-yellow-400">{t('unableLoadReferral')}</p>
                  <p className="text-gray-500 text-sm mt-1 mb-4">{t('tokenMayChanged')}</p>
                  <div className="flex gap-2 justify-center">
                    <Button
                      onClick={async () => {
                        setReferralInfo(null);
                        const guestToken = localStorage.getItem('guestToken');
                        if (guestToken) {
                          try {
                            const response = await fetch(`${API}/user/referral-info?token=${guestToken}`);
                            const data = await response.json();
                            setReferralInfo(data);
                          } catch (err) {
                            setReferralInfo({ success: false, message: err.message });
                          }
                        }
                      }}
                      variant="outline"
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t('retry')}
                    </Button>
                    <Button
                      onClick={() => {
                        localStorage.removeItem('guestToken');
                        localStorage.removeItem('guestUser');
                        window.location.reload();
                      }}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('logout')}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Referral Link */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-200">{t('yourReferralLink')}</label>
                    <div className="flex gap-2">
                      <Input
                        readOnly
                        value={referralInfo.referral_link || ''}
                        className="bg-white/10 border-white/20 text-white text-sm"
                      />
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(referralInfo.referral_link || '');
                          toast.success(t('copied'));
                        }}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Referral Code */}
                  <div className="flex items-center justify-center gap-2 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <span className="text-gray-400 text-sm">{t('yourCode')}:</span>
                    <span className="text-2xl font-bold text-purple-300 tracking-wider">{referralInfo.referral_code || 'N/A'}</span>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center justify-center mb-1">
                        <Users className="w-5 h-5 text-blue-400" />
                      </div>
                      <p className="text-2xl font-bold text-white">{referralInfo.referral_count || 0}</p>
                      <p className="text-xs text-gray-400">{t('referrals')}</p>
                    </div>
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center justify-center mb-1">
                        <Gift className="w-5 h-5 text-green-400" />
                      </div>
                      <p className="text-2xl font-bold text-white">{referralInfo.bonus_days || 0}</p>
                      <p className="text-xs text-gray-400">{t('bonusDays')}</p>
                    </div>
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center justify-center mb-1">
                        <Trophy className="w-5 h-5 text-yellow-400" />
                      </div>
                      <p className="text-2xl font-bold text-white">{referralInfo.manual_extension_days || 0}</p>
                      <p className="text-xs text-gray-400">{t('extension')}</p>
                    </div>
                  </div>

                  {/* How it works */}
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-300 mb-2">{t('howItWorks')}</h4>
                    <ol className="text-xs text-gray-300 space-y-1">
                      <li>{t('howItWorksStep1')}</li>
                      <li>{t('howItWorksStep2')}</li>
                      <li>{t('howItWorksStep3')}</li>
                    </ol>
                  </div>

                  {/* Share Buttons */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        const text = `${t('joinMeOnTrading')} ${referralInfo.referral_link}`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      {t('shareOnWhatsApp')}
                    </Button>
                    <Button
                      onClick={() => {
                        const text = `${t('joinMeOnTrading')} ${referralInfo.referral_link}`;
                        window.open(`https://t.me/share/url?url=${encodeURIComponent(referralInfo.referral_link)}&text=${encodeURIComponent(text)}`, '_blank');
                      }}
                      variant="outline"
                      className="flex-1 border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    >
                      {t('shareOnTelegram')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
        </> 
      )}
      {/* End of logged-in user content */}

      {/* Auth Modal - Always available for visitors */}
      <GuestAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleGuestLoginSuccess}
      />

      {/* Payment Modal - For users with force_payment enabled */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        user={guestUser}
        userEmail={guestUser?.email}
        forcePayment={guestUser?.force_payment === true}
        onPaymentSubmitted={() => {
          toast.success(t('paymentProofAwait'));
          setShowPaymentModal(false);
        }}
      />

      {/* Renewal Modal for Expired Subscriptions */}
      <RenewalModal
        isOpen={showRenewalModal}
        onClose={() => setShowRenewalModal(false)}
        currentPlan={guestUser?.current_plan}
        expiryDate={guestUser?.subscription_expires}
        userEmail={guestUser?.email}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        user={guestUser}
        onUpgradeSuccess={() => {
          // Refresh user data
          if (guestUser?.email) {
            fetch(`${API}/guest/status?email=${encodeURIComponent(guestUser.email)}`)
              .then(res => res.json())
              .then(data => {
                if (data.user) {
                  setGuestUser(prev => ({ ...prev, ...data.user }));
                }
              });
          }
        }}
      />

      {/* PRO Upgrade Promo Slideshow */}
      <ProUpgradePromo
        isOpen={showProPromo}
        onClose={() => setShowProPromo(false)}
        onUpgrade={() => {
          setShowProPromo(false);
          setShowUpgradeModal(true);
        }}
      />

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={guestUser}
        onProfileUpdate={(updatedUser) => {
          setGuestUser(updatedUser);
        }}
      />

      {/* Messenger Modal */}
      <UserMessengerModal
        isOpen={showMessenger}
        onClose={() => setShowMessenger(false)}
      />

      {/* Coaching/Booking Modal */}
      {showCoachingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">1-on-1 Coaching</h2>
                  <p className="text-xs text-gray-400">
                    {coachingStep === 'packages' && 'Select a coaching package'}
                    {coachingStep === 'calendar' && 'Choose your preferred date & time'}
                    {coachingStep === 'payment' && 'Complete payment to confirm'}
                    {coachingStep === 'confirmation' && 'Booking submitted!'}
                    {coachingStep === 'appointments' && 'Your coaching appointments'}
                  </p>
                </div>
              </div>
              <button
                onClick={resetCoachingModal}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Tab Navigation - Show only when not in payment/confirmation flow */}
            {(coachingStep === 'packages' || coachingStep === 'appointments') && (
              <div className="flex border-b border-white/10">
                <button
                  onClick={() => setCoachingStep('packages')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                    coachingStep === 'packages'
                      ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-500/5'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Book New Session
                </button>
                <button
                  onClick={() => setCoachingStep('appointments')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
                    coachingStep === 'appointments'
                      ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-500/5'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  My Appointments
                  {userCoachingBookings.length > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 bg-purple-500 text-white text-xs rounded-full">
                      {userCoachingBookings.length}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Modal Content */}
            <div className="p-4">
              {/* My Appointments View */}
              {coachingStep === 'appointments' && (
                <div className="space-y-4">
                  {loadingBookings ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-purple-400" />
                    </div>
                  ) : userCoachingBookings.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                        <User className="w-8 h-8 text-gray-500" />
                      </div>
                      <h3 className="text-lg font-medium text-white mb-2">No Appointments Yet</h3>
                      <p className="text-gray-400 text-sm mb-4">
                        You have not booked any coaching sessions yet.
                      </p>
                      <Button
                        onClick={() => setCoachingStep('packages')}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                      >
                        Book Your First Session
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Info Banner */}
                      <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <p className="text-xs text-blue-300">
                          <strong>📧 Note:</strong> Once your payment is approved, you&apos;ll receive an email with session details and the link to join.
                        </p>
                      </div>

                      {/* Bookings List */}
                      <div className="space-y-3">
                        {userCoachingBookings.map((booking) => (
                          <div 
                            key={booking.booking_id}
                            className={`p-4 rounded-xl border ${
                              booking.status === 'confirmed' 
                                ? 'bg-green-500/10 border-green-500/30' 
                                : booking.status === 'cancelled'
                                ? 'bg-red-500/10 border-red-500/30'
                                : 'bg-yellow-500/10 border-yellow-500/30'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="font-bold text-white">{booking.package_name}</h4>
                                <p className="text-sm text-gray-400">{booking.duration_minutes} minutes</p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                booking.status === 'confirmed' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : booking.status === 'cancelled'
                                  ? 'bg-red-500/20 text-red-400'
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {booking.status === 'confirmed' ? '✓ Confirmed' 
                                  : booking.status === 'cancelled' ? '✗ Cancelled'
                                  : '⏳ Pending Approval'}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                              <div>
                                <p className="text-gray-500">Date</p>
                                <p className="text-white font-medium">{booking.date}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Time</p>
                                <p className="text-white font-medium">{booking.time_slot} EST</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Amount</p>
                                <p className="text-green-400 font-bold">${booking.price}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Booked On</p>
                                <p className="text-white text-xs">{new Date(booking.created_at).toLocaleDateString()}</p>
                              </div>
                            </div>

                            {/* Status-specific messages */}
                            {booking.status === 'pending' && (
                              <div className="p-2 bg-yellow-500/10 rounded-lg">
                                <p className="text-xs text-yellow-300">
                                  ⏳ Your payment is being reviewed. You&apos;ll receive an email with session instructions once approved.
                                </p>
                              </div>
                            )}
                            
                            {booking.status === 'confirmed' && (
                              <div className="space-y-2">
                                <div className="p-2 bg-green-500/10 rounded-lg">
                                  <p className="text-xs text-green-300">
                                    ✅ Your session is confirmed! Check your email for the joining link and instructions.
                                  </p>
                                </div>
                                <a 
                                  href="https://surl.li/icqdju" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                                >
                                  Join Session →
                                </a>
                              </div>
                            )}

                            {booking.status === 'cancelled' && booking.cancel_reason && (
                              <div className="p-2 bg-red-500/10 rounded-lg">
                                <p className="text-xs text-red-300">
                                  Reason: {booking.cancel_reason}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Cancellation Notice */}
                      <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-xs text-red-300 font-medium mb-1">⚠️ Need to Cancel?</p>
                        <p className="text-xs text-gray-400">
                          Contact support at <a href="mailto:worood@forthewisepro.com" className="text-red-300 underline">worood@forthewisepro.com</a> or via WhatsApp to cancel your appointment.
                          <br/><br/>
                          <strong className="text-red-300">Cancellation Policy:</strong> 50% fee for cancellations with less than 1 hour notice or no-show.
                        </p>
                      </div>

                      {/* Book Another Button */}
                      <Button
                        onClick={() => setCoachingStep('packages')}
                        variant="outline"
                        className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                      >
                        Book Another Session
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* Step 1: Package Selection */}
              {coachingStep === 'packages' && (
                <div className="space-y-4">
                  {/* 1 Hour Package */}
                  <div
                    onClick={() => setSelectedCoachingPackage('1hr')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedCoachingPackage === '1hr'
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-white/10 hover:border-white/30 bg-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                          <span className="text-xl">⏰</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">1-Hour Session</h3>
                          <p className="text-xs text-gray-400">Comprehensive coaching</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-black text-green-400">$100</p>
                        <p className="text-xs text-gray-500">60 minutes</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-300 font-medium">What is included:</p>
                      <ul className="text-xs text-gray-400 space-y-1">
                        <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> Market structure & how the market moves</li>
                        <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> Strategy to take advantage of market reactions</li>
                        <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> Understanding key zones</li>
                        <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> Recognizing manipulation vs natural behavior</li>
                        <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> Q&A related to the coaching session</li>
                      </ul>
                    </div>
                  </div>

                  {/* 30 Min Package */}
                  <div
                    onClick={() => setSelectedCoachingPackage('30min')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedCoachingPackage === '30min'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-white/10 hover:border-white/30 bg-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center">
                          <span className="text-xl">⚡</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">30-Minute Session</h3>
                          <p className="text-xs text-gray-400">Quick start guide</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-black text-green-400">$55</p>
                        <p className="text-xs text-gray-500">30 minutes</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-300 font-medium">What is included:</p>
                      <ul className="text-xs text-gray-400 space-y-1">
                        <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" /> How to use the trading tool effectively</li>
                        <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" /> Understanding market structure</li>
                        <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" /> How the market moves</li>
                        <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" /> What to look for during market movements</li>
                      </ul>
                    </div>
                  </div>

                  {/* Custom Package */}
                  <div
                    onClick={() => setSelectedCoachingPackage('custom')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedCoachingPackage === 'custom'
                        ? 'border-yellow-500 bg-yellow-500/10'
                        : 'border-white/10 hover:border-white/30 bg-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-xl flex items-center justify-center">
                          <span className="text-xl">🎯</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">Custom Package</h3>
                          <p className="text-xs text-gray-400">Tailored to your needs</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-yellow-400">Contact for pricing</p>
                      </div>
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-xs text-yellow-300">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      <strong>Disclaimer:</strong> This is educational content only. Not financial advice.
                    </p>
                  </div>

                  {/* Cancellation Policy Notice */}
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-xs text-red-300 font-medium mb-1">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      Cancellation Policy
                    </p>
                    <p className="text-xs text-gray-400">
                      <strong className="text-red-300">50% cancellation fee</strong> for cancellations with less than 1 hour notice or no-show. You will be refunded half of your payment.
                    </p>
                  </div>

                  {/* Continue Button */}
                  <Button
                    onClick={() => {
                      if (selectedCoachingPackage === 'custom') {
                        // For custom, show contact info
                        toast.info('For custom packages, please contact us at worood@forthewisepro.com');
                        return;
                      }
                      if (!selectedCoachingPackage) {
                        toast.error('Please select a package');
                        return;
                      }
                      if (!guestUser) {
                        toast.error('Please log in to book a session');
                        setShowAuthModal(true);
                        return;
                      }
                      setCoachingStep('calendar');
                      fetchCoachingAvailability(viewingMonth, viewingYear);
                    }}
                    disabled={!selectedCoachingPackage}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white py-3"
                  >
                    Continue to Select Date & Time
                  </Button>
                </div>
              )}

              {/* Step 2: Calendar Selection */}
              {coachingStep === 'calendar' && (
                <div className="space-y-4">
                  {/* Selected Package Summary */}
                  <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <p className="text-sm text-purple-300">
                      Selected: <strong>{selectedCoachingPackage === '1hr' ? '1-Hour Session ($100)' : '30-Minute Session ($55)'}</strong>
                    </p>
                  </div>

                  {/* Month Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => {
                        const newMonth = viewingMonth === 0 ? 11 : viewingMonth - 1;
                        const newYear = viewingMonth === 0 ? viewingYear - 1 : viewingYear;
                        setViewingMonth(newMonth);
                        setViewingYear(newYear);
                        fetchCoachingAvailability(newMonth, newYear);
                      }}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <ChevronDown className="w-5 h-5 text-gray-400 rotate-90" />
                    </button>
                    <p className="text-lg font-bold text-white">
                      {new Date(viewingYear, viewingMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </p>
                    <button
                      onClick={() => {
                        const newMonth = viewingMonth === 11 ? 0 : viewingMonth + 1;
                        const newYear = viewingMonth === 11 ? viewingYear + 1 : viewingYear;
                        setViewingMonth(newMonth);
                        setViewingYear(newYear);
                        fetchCoachingAvailability(newMonth, newYear);
                      }}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <ChevronDown className="w-5 h-5 text-gray-400 -rotate-90" />
                    </button>
                  </div>

                  {/* Available Dates */}
                  <div className="space-y-2">
                    <p className="text-sm text-gray-400">Available dates (EST timezone):</p>
                    {coachingAvailability.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {[...new Set(coachingAvailability.map(a => a.date))].sort().map(date => (
                          <button
                            key={date}
                            onClick={() => {
                              setSelectedCoachingDate(date);
                              setSelectedCoachingTime(null);
                            }}
                            className={`p-3 rounded-lg text-sm font-medium transition-all ${
                              selectedCoachingDate === date
                                ? 'bg-purple-600 text-white'
                                : 'bg-white/5 text-gray-300 hover:bg-white/10'
                            }`}
                          >
                            {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center bg-white/5 rounded-lg">
                        <p className="text-gray-500">No available slots this month. Please check next month.</p>
                      </div>
                    )}
                  </div>

                  {/* Available Time Slots for Selected Date */}
                  {selectedCoachingDate && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-400">Available times for {new Date(selectedCoachingDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}:</p>
                      <div className="grid grid-cols-4 gap-2">
                        {coachingAvailability
                          .filter(a => a.date === selectedCoachingDate)
                          .map(slot => (
                            <button
                              key={slot.slot_id}
                              onClick={() => setSelectedCoachingTime(slot.time_slot)}
                              className={`p-2 rounded-lg text-sm font-medium transition-all ${
                                selectedCoachingTime === slot.time_slot
                                  ? 'bg-green-600 text-white'
                                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
                              }`}
                            >
                              {slot.time_slot} EST
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setCoachingStep('packages')}
                      variant="outline"
                      className="flex-1 border-white/10"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={bookCoachingSession}
                      disabled={!selectedCoachingDate || !selectedCoachingTime || coachingLoading}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                    >
                      {coachingLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                      Reserve & Continue to Payment
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Payment */}
              {coachingStep === 'payment' && (
                <div className="space-y-4">
                  {/* Booking Summary */}
                  <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl">
                    <h3 className="text-lg font-bold text-white mb-3">📋 Booking Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Package:</span>
                        <span className="text-white font-medium">{selectedCoachingPackage === '1hr' ? '1-Hour Session' : '30-Minute Session'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Date:</span>
                        <span className="text-white font-medium">{selectedCoachingDate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Time:</span>
                        <span className="text-white font-medium">{selectedCoachingTime} EST</span>
                      </div>
                      <div className="flex justify-between border-t border-white/10 pt-2 mt-2">
                        <span className="text-gray-400 font-medium">Total:</span>
                        <span className="text-2xl font-black text-green-400">${selectedCoachingPackage === '1hr' ? '100' : '55'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Crypto Payment Instructions */}
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                    <h3 className="text-lg font-bold text-white mb-3">💳 Payment Instructions</h3>
                    <p className="text-sm text-gray-300 mb-4">
                      Send <strong className="text-green-400">${selectedCoachingPackage === '1hr' ? '100' : '55'}</strong> in crypto to the wallet below:
                    </p>
                    
                    <div className="p-3 bg-black/40 rounded-lg mb-4">
                      <p className="text-xs text-gray-500 mb-1">Wallet Address (USDT/BTC/ETH):</p>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-cyan-400 break-all flex-1">0x8c492198161A497276475B57880E1F2FD20e192D</code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText('0x8c492198161A497276475B57880E1F2FD20e192D');
                            toast.success('Wallet address copied!');
                          }}
                          className="p-2 hover:bg-white/10 rounded transition-colors"
                        >
                          <Copy className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                    </div>

                    {/* QR Code */}
                    <div className="flex justify-center mb-4">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_b4c8a9d0-7ea2-48c0-817a-eed35237bb3f/artifacts/0i16gwr9_qrccc.jpeg" 
                        alt="Payment QR Code" 
                        className="w-40 h-40 rounded-lg border border-white/20"
                      />
                    </div>

                    {/* Upload Payment Proof */}
                    <div className="space-y-2">
                      <p className="text-sm text-gray-400">After payment, upload your transaction screenshot:</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCoachingFileUpload}
                        className="hidden"
                        id="coaching-payment-proof"
                      />
                      <label
                        htmlFor="coaching-payment-proof"
                        className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                          coachingPaymentProof
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-white/20 hover:border-white/40'
                        }`}
                      >
                        {coachingPaymentProof ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <span className="text-green-400">{coachingPaymentProof.filename}</span>
                          </>
                        ) : (
                          <>
                            <Share2 className="w-5 h-5 text-gray-400" />
                            <span className="text-gray-400">Click to upload screenshot</span>
                          </>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setCoachingStep('calendar')}
                      variant="outline"
                      className="flex-1 border-white/10"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={submitCoachingPaymentProof}
                      disabled={!coachingPaymentProof || coachingLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      {coachingLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                      Submit Payment Proof
                    </Button>
                  </div>

                  {/* Cancellation Notice */}
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-xs text-red-300 font-medium mb-1">⚠️ Need to Cancel?</p>
                    <p className="text-xs text-gray-400">
                      Contact support at <a href="mailto:worood@forthewisepro.com" className="text-red-300 underline">worood@forthewisepro.com</a> or via WhatsApp if you need to cancel this booking.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 4: Confirmation */}
              {coachingStep === 'confirmation' && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-10 h-10 text-yellow-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Payment Submitted!</h3>
                  <p className="text-gray-400 mb-4">
                    Your booking is <strong className="text-yellow-400">pending approval</strong>. Once we verify your payment, you&apos;ll receive a confirmation email with session details and the link to join.
                  </p>
                  
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl text-left mb-4">
                    <h4 className="text-sm font-bold text-white mb-2">📋 Your Booking Details:</h4>
                    <div className="space-y-1 text-sm">
                      <p className="text-gray-400">Package: <span className="text-white">{selectedCoachingPackage === '1hr' ? '1-Hour Session' : '30-Minute Session'}</span></p>
                      <p className="text-gray-400">Date: <span className="text-white">{selectedCoachingDate}</span></p>
                      <p className="text-gray-400">Time: <span className="text-white">{selectedCoachingTime} EST</span></p>
                      <p className="text-gray-400">Amount: <span className="text-green-400 font-bold">${selectedCoachingPackage === '1hr' ? '100' : '55'}</span></p>
                      <p className="text-gray-400">Status: <span className="text-yellow-400 font-medium">⏳ Pending Approval</span></p>
                    </div>
                  </div>

                  {/* What&apos;s Next */}
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl text-left mb-4">
                    <h4 className="text-sm font-bold text-blue-400 mb-2">📧 What&apos;s Next?</h4>
                    <ul className="text-xs text-gray-300 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400">1.</span>
                        We&apos;ll review your payment (usually within a few hours)
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400">2.</span>
                        Once approved, you&apos;ll receive an email with your session link and instructions
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400">3.</span>
                        Join the session at the scheduled time using the link in your email
                      </li>
                    </ul>
                  </div>

                  {/* Session Join Link (for reference) */}
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl mb-4">
                    <h4 className="text-sm font-bold text-green-400 mb-2">🔗 Session Link (Save This!)</h4>
                    <p className="text-gray-400 text-xs mb-3">
                      You&apos;ll also receive this in your confirmation email:
                    </p>
                    <a 
                      href="https://surl.li/icqdju" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Join Session →
                    </a>
                    <p className="text-green-400/70 text-xs mt-2 break-all">
                      https://surl.li/icqdju
                    </p>
                  </div>

                  {/* Cancellation Notice */}
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-left mb-4">
                    <h4 className="text-sm font-bold text-red-400 mb-2">⚠️ Need to Cancel?</h4>
                    <p className="text-gray-300 text-xs leading-relaxed mb-2">
                      Contact support at <a href="mailto:worood@forthewisepro.com" className="text-red-300 underline">worood@forthewisepro.com</a> or via WhatsApp to cancel your appointment.
                    </p>
                    <p className="text-gray-400 text-xs">
                      <strong className="text-red-300">Cancellation Policy:</strong> 50% fee for cancellations with less than 1 hour notice or no-show.
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        fetchUserCoachingBookings();
                        setCoachingStep('appointments');
                      }}
                      variant="outline"
                      className="flex-1 border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                    >
                      View My Appointments
                    </Button>
                    <Button
                      onClick={resetCoachingModal}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestPortal;
